import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { Tournament, TournamentCustomGroup, TournamentPitch } from 'Src/models/tournament';
import { TournamentTeam } from 'Src/models/tournamentTeam';
import { SchoolEvent } from 'Src/models/event';
import { Sport } from 'Src/models/sport';
import propz from 'propz';
import Lazy from 'lazy.js';
import { isIndividualTournament, isMiniTeamTournament, isTeamTournament } from 'Src/helpers/tournament/tournament';
import {
  getSchoolUnionTournamentTeams,
  getSchoolUnionTournamentTeamsCount,
  getAllSchoolUnionTournamentTeams
} from 'Src/helpers/service/adminSU/tournamentTeamsTable';
import { getTeamGender } from 'Src/helpers/accessor/accessor';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import {
  addSchoolTournamentTeam,
  removeSchoolTournamentTeam,
  editSchoolTournamentTeam
} from 'Src/helpers/service/admin/tournamentTeams';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { TournamentAddEventForm } from '../TournamentAddEventForm/TournamentAddEventForm';
import { TournamentEventsGroupForm } from '../TournamentEventsGroupForm/TournamentEventsGroupsForm';
import { AdminSUTournamentTeamForm } from '../TournamentTeamForm/TournamentTeamForm';
import { TEAM_TYPE } from 'Src/consts/team';
import {
  createTournamentGroup,
  deleteTournamentGroup,
  editTournamentGroup,
  getTournamentGroupDeletionCheck,
  getTournamentCustomGroups
} from 'Src/helpers/service/adminSU/tournamentCustomGroupsTable';
import { TournamentDefaultGroupsForm } from '../TournamentDefaultGroupsForms/TournamentDefaultGroupsForm';
import { UnsavedChangesModal } from 'Src/components/UnsavedChangesModal/UnsavedChangesModal';
import {
  createPitch,
  deletePitch,
  getTournamentPitches,
  getTournamentPitchesCount,
  updatePitch
} from 'Src/helpers/service/adminSU/tournamentPitchesTable';
import {
  assignPitchByTournamentIdAndEventId,
  deleteEvent,
  getAllTournamentEvents,
  getTournamentEvents,
  getTournamentEventsCount,
  getTournamentEventSports,
  unassignPitchByTournamentIdAndEventId
} from 'Src/helpers/service/adminSU/tournamentEventsTable';
import { getEventGender, getEventStatus } from 'Src/helpers/accessor/accessor';
import { formatTournamentAges } from 'Src/helpers/ageGroups';
import { getAllTournamentParticipants } from 'Src/helpers/service/adminSU/tournamentParticipantsTable';
import { activateEvent, addGroupEvents, addTeam, createEvent } from 'Src/helpers/service/admin/event';
import { assignTournamentEventNumbers } from 'Src/helpers/service/adminSU/tournamentsTable';
import { assignSchoolUnionTournamentTeamPlayerNumbersToTournament } from 'Src/helpers/service/adminSU/tournamentTeamPlayersTable';
import { DEFAULT_POINT, EVENT_VENUE } from 'Src/consts/venue';
import { EVENT_STATUS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/event';
import { SearchMultiSelectFilter } from 'Src/components/SearchMultiSelectFilter/SearchMultiSelectFilter';
import {
  TOURNAMENT_FINAL_STAGE,
  TOURNAMENT_GROUP_STAGE,
  TOURNAMENT_GROUP_TAG_MAPPING,
  TOURNAMENT_LAYOUT_TYPE,
  TOURNAMENT_PLAYOFF_STAGE,
  TOURNAMENT_QUALIFIER_STAGE,
  TOURNAMENT_SCHOOL_LOGO_FALLBACK
} from 'Src/consts/tournament';
import { TournamentCompetitionStructure } from 'Src/components/TournamentCompetitionStructure/TournamentCompetitionStructure';
import { TournamentCompetitorsSummary } from 'Src/components/TournamentCompetitorsSummary/TournamentCompetitorsSummary';

export type CompetitionSection = 'teams' | 'groups' | 'pitches' | 'fixtures' | 'competitors' | 'events';

export interface TournamentEventListState {
  page: number;
  sportFilters: string[];
  ageFilters: string[];
  genderFilters: string[];
  groupFilters: string[];
  pitchFilters: string[];
  stageFilters: string[];
  statusFilters: string[];
}

const getUniqueEventAthleteCount = (event: SchoolEvent): number => {
  const athletes = [
    ...propz.get<any[]>(event, ['players'], []),
    ...propz.get<any[]>(event, ['individuals'], [])
  ];
  const athleteKeys = athletes
    .map(athlete => athlete && (athlete.userId || athlete.permissionId || athlete.id || athlete._id))
    .filter(Boolean)
    .map(String);
  return new Set(athleteKeys).size;
};

const GROUP_STAGE_FILTER = 'GROUP_STAGE';
const KNOCKOUT_ROUND_OPTIONS = [
  { id: 'ROUND_OF_32', name: 'Round of 32' },
  { id: 'ROUND_OF_16', name: 'Round of 16' },
  { id: 'ROUND_OF_8', name: 'Round of 8' },
  { id: 'QUARTER_FINAL', name: 'Quarter-final' },
  { id: 'SEMI_FINAL', name: 'Semi-final' },
  { id: 'FINAL', name: 'Final' },
  { id: 'PLAYOFF', name: 'Play-off / placing' }
];

interface Props {
  user: AppUser;
  tournament: Tournament;
  activeSection: CompetitionSection;
  onSectionChange: (section: CompetitionSection) => void;
  onAddTeam: () => void;
  onAssignTournamentNumbers: () => void;
  onManageTeam: (team: TournamentTeam) => void;
  onOpenGroupsGrid: () => void;
  onManageGroups: () => void;
  onOpenCompetitionReports: () => void;
  onOpenLegacyEvents: () => void;
  onOpenEvent: (event: SchoolEvent, listState: TournamentEventListState) => void;
  eventListState?: TournamentEventListState;
  onUpdated?: (tournament:Tournament)=>void;
}

interface State {
  unassignedTeams: number;
  teams: TournamentTeam[];
  teamsPage: number;
  teamsTotal: number;
  teamsUnfilteredTotal: number | null;
  teamsSearch: string;
  teamsSearchDraft: string;
  isTeamsLoading: boolean;
  selectedTeamIds: string[];
  openedTeam?: TournamentTeam;
  teamDrawerTab: 'details' | 'members';
  editedTeamName: string;
  isSavingTeam: boolean;
  isDeleteTeamsOpen: boolean;
  isDeletingTeams: boolean;
  deleteTeamsError: string;
  isTeamFiltersOpen: boolean;
  isTeamColumnsOpen: boolean;
  isAssignTeamNumbersOpen: boolean;
  assignTeamNumbersFrom: string;
  assignTeamNumbersMode: 'FILL_MISSING' | 'RENUMBER_ALL';
  isAssigningTeamNumbers: boolean;
  assignTeamNumbersError: string;
  teamGenderFilters: string[];
  teamGroupFilter: string;
  teamGenderFilterDraft: string[];
  teamGroupFilterDraft: string;
  visibleTeamColumns: string[];
  isAddTeamOpen: boolean;
  allTournamentTeams: TournamentTeam[];
  isAddingTeam: boolean;
  pendingTeamData?: any;
  teamLimitWarnings: string[];
  isTeamLimitWarningOpen: boolean;
  groups: TournamentCustomGroup[];
  groupTeams: TournamentTeam[];
  selectedGroupIds: string[];
  isDefaultGroupsOpen: boolean;
  isSavingDefaultGroups: boolean;
  isDeleteGroupsOpen: boolean;
  isDeletingGroups: boolean;
  groupsSearch: string;
  groupsAppliedSearch: string;
  openedGroup?: TournamentCustomGroup;
  editedGroupName: string;
  editedGroupStageId: string;
  isSavingGroup: boolean;
  isDiscardGroupChangesOpen: boolean;
  isAddCustomGroupOpen: boolean;
  newGroupName: string;
  newGroupStageId: string;
  isCreatingCustomGroup: boolean;
  groupFormError: string;
  deleteGroupsError: string;
  pitches: TournamentPitch[];
  pitchesPage: number;
  pitchesTotal: number;
  pitchesUnfilteredTotal: number | null;
  pitchesSearch: string;
  pitchesSearchDraft: string;
  isPitchesLoading: boolean;
  selectedPitchIds: string[];
  isAddPitchOpen: boolean;
  openedPitch?: TournamentPitch;
  pitchName: string;
  pitchSurface: string;
  pitchSize: string;
  pitchNote: string;
  pitchFormError: string;
  isSavingPitch: boolean;
  isDeletePitchesOpen: boolean;
  isDeletingPitches: boolean;
  isDiscardPitchChangesOpen: boolean;
  events: SchoolEvent[];
  eventsPage: number;
  eventsTotal: number;
  eventsUnfilteredTotal: number | null;
  isEventsLoading: boolean;
  eventSportOptions: Sport[];
  eventSportFilter: string[];
  eventAgeFilter: string[];
  eventGenderFilter: string[];
  eventGroupFilter: string[];
  eventPitchFilter: string[];
  eventStageFilter: string[];
  eventStatusFilter: string[];
  eventSportFilterDraft: string[];
  eventAgeFilterDraft: string[];
  eventGenderFilterDraft: string[];
  eventGroupFilterDraft: string[];
  eventPitchFilterDraft: string[];
  eventStageFilterDraft: string[];
  eventStatusFilterDraft: string[];
  isEventFiltersOpen: boolean;
  isEventColumnsOpen: boolean;
  hiddenEventColumns: string[];
  selectedEventIds: string[];
  isDeleteEventsOpen: boolean;
  isDeletingEvents: boolean;
  isAssignEventPitchOpen: boolean;
  selectedEventPitchId: string;
  isAssigningEventPitch: boolean;
  isAddFixtureOpen: boolean;
  isFixtureDirty: boolean;
  isDiscardFixtureChangesOpen: boolean;
  fixtureParticipants: any[];
  isFixtureActionLoading: boolean;
  isCreateGroupEventsOpen: boolean;
  isReplaceGroupEventsOpen: boolean;
  pendingGroupEventsData?: any;
  replaceGroupEventsCount: number;
  groupEventsSuccess?: { groupName: string; createdCount: number; replacedCount: number };
  isAssignEventNumbersOpen: boolean;
  assignEventNumbersFrom: string;
  fixtureActionError: string;
  fixtureActionSuccess: string;
}

const Icon = ({ path }: { path: string }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
    <path d={path} />
  </svg>
);

const TEAM_SECTIONS: Array<{ key: CompetitionSection; label: string; icon: string }> = [
  {
    key: 'teams',
    label: 'Teams',
    icon:
      'M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M8.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.8M16 3.2a4 4 0 0 1 0 7.6'
  },
  { key: 'groups', label: 'Structure', icon: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z' },
  { key: 'pitches', label: 'Pitches', icon: 'M3 5h18v14H3zM12 5v14M3 12h18M15 12a3 3 0 1 1-6 0 3 3 0 1 1 6 0' },
  { key: 'fixtures', label: 'Fixtures', icon: 'M7 3v3M17 3v3M4 8h16M5 5h14a1 1 0 0 1 1 1v14H4V6a1 1 0 0 1 1-1z' }
];

const INDIVIDUAL_SECTIONS: Array<{ key: CompetitionSection; label: string; icon: string }> = [
  {
    key: 'competitors',
    label: 'Competitors',
    icon: 'M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M8.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8'
  },
  {
    key: 'teams',
    label: 'Teams',
    icon:
      'M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M8.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.8M16 3.2a4 4 0 0 1 0 7.6'
  },
  { key: 'events', label: 'Events', icon: 'M7 3v3M17 3v3M4 8h16M5 5h14a1 1 0 0 1 1 1v14H4V6a1 1 0 0 1 1-1z' }
];

const FIXTURE_COLUMNS = [
  { key: 'number', label: 'Event number' },
  { key: 'time', label: 'Time' },
  { key: 'stage', label: 'Stage' },
  { key: 'pitch', label: 'Pitch' },
  { key: 'fixture', label: 'Fixture', required: true },
  { key: 'score', label: 'Score' },
  { key: 'status', label: 'Status' }
];

const fixtureColumnsStorageKey = (userId: string) => `columnVisibility__${userId || 'anonymous'}__tournamentFixtures`;
const teamColumnsStorageKey = (userId: string) => `columnVisibility__${userId || 'anonymous'}__tournamentTeams`;
const DEFAULT_TEAM_COLUMNS = ['school', 'age', 'gender', 'groups', 'players'];

const EVENT_STATUS_OPTIONS = ['ACCEPTED', 'FINISHED', 'CANCELED'].map(id => ({
  id,
  name: EVENT_STATUS_SERVER_TO_CLIENT_MAPPING[id]
}));

const loadHiddenFixtureColumns = (userId: string): string[] => {
  try {
    const saved = JSON.parse(localStorage.getItem(fixtureColumnsStorageKey(userId)) || '[]');
    return Array.isArray(saved) ? saved.filter(key => key !== 'fixture') : [];
  } catch {
    return [];
  }
};

const loadVisibleTeamColumns = (userId: string): string[] => {
  try {
    const saved = JSON.parse(localStorage.getItem(teamColumnsStorageKey(userId)) || '[]');
    return Array.isArray(saved) && saved.length ? saved : DEFAULT_TEAM_COLUMNS;
  } catch {
    return DEFAULT_TEAM_COLUMNS;
  }
};

export class TournamentCompetition extends React.Component<Props, State> {
  private isCreatingFixture = false;

  state: State = {
    unassignedTeams: 0,
    teams: [],
    teamsPage: 1,
    teamsTotal: 0,
    teamsUnfilteredTotal: null,
    teamsSearch: '',
    teamsSearchDraft: '',
    isTeamsLoading: false,
    selectedTeamIds: [],
    teamDrawerTab: 'details',
    editedTeamName: '',
    isSavingTeam: false,
    isDeleteTeamsOpen: false,
    isDeletingTeams: false,
    deleteTeamsError: '',
    isTeamFiltersOpen: false,
    isTeamColumnsOpen: false,
    isAssignTeamNumbersOpen: false,
    assignTeamNumbersFrom: '1',
    assignTeamNumbersMode: 'FILL_MISSING',
    isAssigningTeamNumbers: false,
    assignTeamNumbersError: '',
    teamGenderFilters: [],
    teamGroupFilter: '',
    teamGenderFilterDraft: [],
    teamGroupFilterDraft: '',
    visibleTeamColumns: loadVisibleTeamColumns(this.props.user.userId),
    isAddTeamOpen: false,
    allTournamentTeams: [],
    isAddingTeam: false,
    teamLimitWarnings: [],
    isTeamLimitWarningOpen: false,
    groups: [],
    groupTeams: [],
    selectedGroupIds: [],
    isDefaultGroupsOpen: false,
    isSavingDefaultGroups: false,
    isDeleteGroupsOpen: false,
    isDeletingGroups: false,
    groupsSearch: '',
    groupsAppliedSearch: '',
    editedGroupName: '',
    editedGroupStageId: '',
    isSavingGroup: false,
    isDiscardGroupChangesOpen: false,
    isAddCustomGroupOpen: false,
    newGroupName: '',
    newGroupStageId: '',
    isCreatingCustomGroup: false,
    groupFormError: '',
    deleteGroupsError: '',
    pitches: [],
    pitchesPage: 1,
    pitchesTotal: 0,
    pitchesUnfilteredTotal: null,
    pitchesSearch: '',
    pitchesSearchDraft: '',
    isPitchesLoading: false,
    selectedPitchIds: [],
    isAddPitchOpen: false,
    pitchName: '',
    pitchSurface: '',
    pitchSize: '',
    pitchNote: '',
    pitchFormError: '',
    isSavingPitch: false,
    isDeletePitchesOpen: false,
    isDeletingPitches: false,
    isDiscardPitchChangesOpen: false,
    events: [],
    eventsPage: 1,
    eventsTotal: 0,
    eventsUnfilteredTotal: null,
    isEventsLoading: false,
    eventSportOptions: [],
    eventSportFilter: [],
    eventAgeFilter: [],
    eventGenderFilter: [],
    eventGroupFilter: [],
    eventPitchFilter: [],
    eventStageFilter: [],
    eventStatusFilter: [],
    eventSportFilterDraft: [],
    eventAgeFilterDraft: [],
    eventGenderFilterDraft: [],
    eventGroupFilterDraft: [],
    eventPitchFilterDraft: [],
    eventStageFilterDraft: [],
    eventStatusFilterDraft: [],
    isEventFiltersOpen: false,
    isEventColumnsOpen: false,
    hiddenEventColumns: loadHiddenFixtureColumns(this.props.user.userId),
    selectedEventIds: [],
    isDeleteEventsOpen: false,
    isDeletingEvents: false,
    isAssignEventPitchOpen: false,
    selectedEventPitchId: '',
    isAssigningEventPitch: false,
    isAddFixtureOpen: false,
    isFixtureDirty: false,
    isDiscardFixtureChangesOpen: false,
    fixtureParticipants: [],
    isFixtureActionLoading: false,
    isCreateGroupEventsOpen: false,
    isReplaceGroupEventsOpen: false,
    pendingGroupEventsData: undefined,
    replaceGroupEventsCount: 0,
    groupEventsSuccess: undefined,
    isAssignEventNumbersOpen: false,
    assignEventNumbersFrom: '1',
    fixtureActionError: '',
    fixtureActionSuccess: ''
  };

  componentDidMount() {
    const restored = this.props.eventListState;
    if (restored && (this.props.activeSection === 'events' || this.props.activeSection === 'fixtures')) {
      this.setState(
        {
          eventsPage: restored.page || 1,
          eventSportFilter: restored.sportFilters || [],
          eventAgeFilter: restored.ageFilters || [],
          eventGenderFilter: restored.genderFilters || [],
          eventGroupFilter: restored.groupFilters || [],
          eventPitchFilter: restored.pitchFilters || [],
          eventStageFilter: restored.stageFilters || [],
          eventStatusFilter: restored.statusFilters || []
        },
        this.loadActiveCompetitionSection
      );
      return;
    }
    this.loadActiveCompetitionSection();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.user.userId !== this.props.user.userId) {
      this.setState({
        hiddenEventColumns: loadHiddenFixtureColumns(this.props.user.userId),
        visibleTeamColumns: loadVisibleTeamColumns(this.props.user.userId)
      });
    }
    if (prevProps.tournament.id !== this.props.tournament.id) {
      this.loadActiveCompetitionSection();
    } else if (prevProps.activeSection !== this.props.activeSection) {
      this.loadActiveCompetitionSection();
    } else if (prevProps.tournament.customGroups !== this.props.tournament.customGroups) {
      this.setState({ groups: this.props.tournament.customGroups || [] });
    }
  }

  isTeamCompetition = () => isTeamTournament(this.props.tournament) || isMiniTeamTournament(this.props.tournament);

  loadActiveCompetitionSection = () => {
    switch (this.props.activeSection) {
      case 'teams':
      case 'groups':
        this.loadCompetitionTeamsAndGroups();
        break;
      case 'pitches':
        this.loadPitches(1, '');
        break;
      case 'fixtures':
      case 'events':
        this.loadGroups();
        this.loadPitches(1, '');
        this.loadEventSportOptions();
        this.loadEvents(this.state.eventsPage);
        break;
      default:
        break;
    }
  };

  loadEventSportOptions = () => {
    getTournamentEventSports(this.props.user, this.props.tournament.id)
      .then(eventSportOptions => this.setState({ eventSportOptions: eventSportOptions || [] }))
      .catch(() => this.setState({ eventSportOptions: [] }));
  };

  loadCompetitionTeamsAndGroups = () => {
    if (!this.isTeamCompetition()) {
      this.loadTeams(1, '');
      return;
    }
    const { tournament, user } = this.props;
    this.setState({ isTeamsLoading: true, groups: tournament.customGroups || [] });
    Promise.all([
      getTournamentCustomGroups(user, tournament.id, { limit: 3000, skip: 0 }),
      getAllSchoolUnionTournamentTeams(user, tournament.id)
    ])
      .then(([groups, groupTeams]) => {
        const sortedTeams = [...groupTeams].sort((left, right) =>
          String(left.name || '').localeCompare(String(right.name || ''))
        );
        const unassignedTeams = sortedTeams.filter(team => !Array.isArray(team.groupIds) || team.groupIds.length === 0)
          .length;
        this.setState({
          groups,
          groupTeams: sortedTeams,
          teams: sortedTeams.slice(0, 20),
          teamsPage: 1,
          teamsTotal: sortedTeams.length,
          teamsUnfilteredTotal: sortedTeams.length,
          teamsSearch: '',
          teamsSearchDraft: '',
          unassignedTeams,
          isTeamsLoading: false
        });
      })
      .catch(() => this.setState({ isTeamsLoading: false }));
  };

  loadGroups = () => {
    if (!this.isTeamCompetition()) return;
    const { tournament, user } = this.props;
    this.setState({ groups: tournament.customGroups || [] });
    Promise.all([
      getTournamentCustomGroups(user, tournament.id, { limit: 3000, skip: 0 }),
      getAllSchoolUnionTournamentTeams(user, tournament.id)
    ])
      .then(([groups, groupTeams]) => this.setState({ groups, groupTeams }))
      .catch(() => {
        getAllSchoolUnionTournamentTeams(user, tournament.id)
          .then(groupTeams => this.setState({ groupTeams }))
          .catch(() => undefined);
      });
  };

  toggleFixtureColumn = (column: string) => {
    if (column === 'fixture') return;
    this.setState(state => {
      const hiddenEventColumns = state.hiddenEventColumns.includes(column)
        ? state.hiddenEventColumns.filter(item => item !== column)
        : [...state.hiddenEventColumns, column];
      try {
        localStorage.setItem(fixtureColumnsStorageKey(this.props.user.userId), JSON.stringify(hiddenEventColumns));
      } catch {}
      return { hiddenEventColumns };
    });
  };

  resetFixtureColumns = () => {
    const hiddenEventColumns: string[] = [];
    try {
      localStorage.setItem(fixtureColumnsStorageKey(this.props.user.userId), JSON.stringify(hiddenEventColumns));
    } catch {}
    this.setState({ hiddenEventColumns });
  };

  toggleTeamColumn = (column: string) => {
    this.setState(state => {
      const visibleTeamColumns = state.visibleTeamColumns.includes(column)
        ? state.visibleTeamColumns.filter(item => item !== column)
        : [...state.visibleTeamColumns, column];
      try {
        localStorage.setItem(teamColumnsStorageKey(this.props.user.userId), JSON.stringify(visibleTeamColumns));
      } catch {}
      return { visibleTeamColumns };
    });
  };

  resetTeamColumns = () => {
    const visibleTeamColumns = [...DEFAULT_TEAM_COLUMNS];
    try {
      localStorage.setItem(teamColumnsStorageKey(this.props.user.userId), JSON.stringify(visibleTeamColumns));
    } catch {}
    this.setState({ visibleTeamColumns });
  };

  toggleEventFilterDraft = (
    field:
      | 'eventSportFilterDraft'
      | 'eventAgeFilterDraft'
      | 'eventGenderFilterDraft'
      | 'eventGroupFilterDraft'
      | 'eventPitchFilterDraft'
      | 'eventStageFilterDraft'
      | 'eventStatusFilterDraft',
    value: string
  ) => {
    this.setState(
      state =>
        ({
          [field]: state[field].includes(value) ? state[field].filter(item => item !== value) : [...state[field], value]
        } as Pick<State, typeof field>)
    );
  };

  removeEventFilter = (
    field:
      | 'eventSportFilter'
      | 'eventAgeFilter'
      | 'eventGenderFilter'
      | 'eventGroupFilter'
      | 'eventPitchFilter'
      | 'eventStageFilter'
      | 'eventStatusFilter',
    value: string
  ) => {
    this.setState(
      state =>
        ({
          [field]: state[field].filter(item => item !== value)
        } as Pick<State, typeof field>),
      () => this.loadEvents(1)
    );
  };

  loadUnassignedTeams = () => {
    if (!this.isTeamCompetition()) return;
    getSchoolUnionTournamentTeamsCount(this.props.user, this.props.tournament.id, {
      $or: [{ groupIds: { $size: 0 } }, { groupIds: { $exists: false } }]
    })
      .then(result => {
        const count = Number((result as any)?.count ?? result ?? 0);
        this.setState({ unassignedTeams: isFinite(count) ? count : 0 });
      })
      .catch(() => this.setState({ unassignedTeams: 0 }));
  };

  loadTeams = (page: number, search: string) => {
    const where: any = {};
    if (search) where.name = { like: search, options: 'i' };
    if (this.state.teamGenderFilters.length === 1) where.gender = this.state.teamGenderFilters[0];
    if (this.state.teamGenderFilters.length > 1) where.gender = { $in: this.state.teamGenderFilters };
    if (this.state.teamGroupFilter === 'UNASSIGNED') where.groupIds = { $size: 0 };
    else if (this.state.teamGroupFilter) where.groupIds = this.state.teamGroupFilter;
    const isFiltered = Boolean(search || this.state.teamGenderFilters.length || this.state.teamGroupFilter);
    this.setState({ isTeamsLoading: true });
    Promise.all([
      getSchoolUnionTournamentTeams(this.props.user, this.props.tournament.id, {
        where,
        limit: 20,
        skip: (page - 1) * 20,
        order: 'name ASC'
      }),
      getSchoolUnionTournamentTeamsCount(this.props.user, this.props.tournament.id, where)
    ])
      .then(([teams, countResult]) => {
        const total = Number((countResult as any)?.count ?? countResult ?? 0);
        this.setState({
          teams,
          teamsPage: page,
          teamsTotal: isFinite(total) ? total : 0,
          teamsUnfilteredTotal: isFiltered ? this.state.teamsUnfilteredTotal : isFinite(total) ? total : 0,
          teamsSearch: search,
          isTeamsLoading: false
        });
      })
      .catch(() => this.setState({ teams: [], isTeamsLoading: false }));
  };

  onTeamsSearch = (event: React.FormEvent) => {
    event.preventDefault();
    this.loadTeams(1, this.state.teamsSearchDraft.trim());
  };

  getGroupNames = (team: TournamentTeam) =>
    (this.props.tournament.customGroups || [])
      .filter(group => (team.groupIds || []).includes(group.id))
      .map(group => group.name);

  saveTeamName = async () => {
    const { openedTeam, editedTeamName } = this.state;
    if (!openedTeam || !editedTeamName.trim() || this.state.isSavingTeam) return;
    this.setState({ isSavingTeam: true });
    try {
      const updated = await editSchoolTournamentTeam(this.props.user, this.props.tournament.id, openedTeam.id, {
        name: editedTeamName.trim()
      });
      this.setState({
        openedTeam: { ...openedTeam, ...(updated || {}), name: editedTeamName.trim() },
        isSavingTeam: false
      });
      this.loadTeams(this.state.teamsPage, this.state.teamsSearch);
    } catch (error) {
      this.setState({ isSavingTeam: false });
    }
  };

  deleteSelectedTeams = async () => {
    if (this.state.isDeletingTeams || this.state.selectedTeamIds.length === 0) return;

    this.setState({ isDeletingTeams: true, deleteTeamsError: '' });
    try {
      const allTeams = await getAllSchoolUnionTournamentTeams(this.props.user, this.props.tournament.id);
      const selectedIds = new Set(this.state.selectedTeamIds.map(String));
      const participantSchoolIds = new Set(
        (this.props.tournament.schoolIds || []).map(participant =>
          String((participant as any)?.schoolId || participant)
        )
      );
      const selectedTeams = allTeams.filter(team => selectedIds.has(String(team.id)));
      const affectedSchoolIds = Array.from(new Set(selectedTeams.map(team => String(team.schoolId))));
      const blockedSchoolNames = affectedSchoolIds
        .filter(schoolId => {
          if (!participantSchoolIds.has(schoolId)) return false;
          return (
            allTeams.filter(team => String(team.schoolId) === schoolId && !selectedIds.has(String(team.id))).length ===
            0
          );
        })
        .map(schoolId => {
          const team = selectedTeams.find(item => String(item.schoolId) === schoolId) as any;
          return team?.school?.name || team?.schoolName || 'A participant school';
        });

      if (blockedSchoolNames.length > 0) {
        this.setState({
          allTournamentTeams: allTeams,
          isDeletingTeams: false,
          deleteTeamsError: `${blockedSchoolNames.join(', ')} must keep at least one team while participating. Remove ${
            blockedSchoolNames.length === 1 ? 'the school' : 'these schools'
          } from Participants instead.`
        });
        return;
      }

      // Keep the requests sequential so the server-side last-team guard cannot be raced by a bulk selection.
      for (const id of this.state.selectedTeamIds) {
        await removeSchoolTournamentTeam(this.props.user, this.props.tournament.id, id);
      }

      this.setState({
        allTournamentTeams: allTeams.filter(team => !selectedIds.has(String(team.id))),
        selectedTeamIds: [],
        isDeleteTeamsOpen: false,
        isDeletingTeams: false,
        deleteTeamsError: ''
      });
      this.loadTeams(1, this.state.teamsSearch);
      this.loadUnassignedTeams();
    } catch (error) {
      const message =
        (error as any)?.response?.data?.error?.message ||
        (error as any)?.response?.data?.details?.text ||
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        'The selected teams could not be deleted. Please try again.';
      this.setState({ isDeletingTeams: false, deleteTeamsError: message });
    }
  };

  closeDeleteTeamsModal = () => {
    if (this.state.isDeletingTeams) return;
    this.setState({ isDeleteTeamsOpen: false, deleteTeamsError: '' });
  };

  openAddTeam = async () => {
    this.setState({ isAddingTeam: true });
    try {
      const allTournamentTeams = await getAllSchoolUnionTournamentTeams(this.props.user, this.props.tournament.id);
      this.setState({ allTournamentTeams, isAddTeamOpen: true, isAddingTeam: false });
    } catch (error) {
      this.setState({ isAddingTeam: false });
    }
  };

  getTeamLimitWarnings = (data: any): string[] => {
    const { tournament } = this.props;
    if (!tournament.isAutoEnrollmentTeams) return [];
    const settings = tournament.autoEnrollmentTeamsSettings || [];
    const warnings: string[] = [];
    const isTotalUnlimited = !settings.length || settings.some(item => Number(item.maximumNumberTeams || 0) === 0);
    const totalCapacity = settings.reduce((sum, item) => sum + Number(item.maximumNumberTeams || 0), 0);
    const currentTotal = this.state.allTournamentTeams.length;
    if (!isTotalUnlimited && currentTotal >= totalCapacity) {
      warnings.push(
        `The tournament capacity is ${totalCapacity} team${
          totalCapacity === 1 ? '' : 's'
        }. Adding this team will make ${currentTotal + 1}.`
      );
    }
    const schoolLimit = Number(tournament.maximumNumberTeamsOfSchool || 0);
    if (schoolLimit > 0) {
      const schoolTeams = this.state.allTournamentTeams.filter(team => String(team.schoolId) === String(data.schoolId))
        .length;
      if (schoolTeams >= schoolLimit) {
        warnings.push(
          `This school can normally provide up to ${schoolLimit} team${
            schoolLimit === 1 ? '' : 's'
          }. Adding this team will make ${schoolTeams + 1}.`
        );
      }
    }
    return warnings;
  };

  addTeam = (data: any) => {
    const teamLimitWarnings = this.getTeamLimitWarnings(data);
    if (teamLimitWarnings.length) {
      this.setState({ pendingTeamData: data, teamLimitWarnings, isTeamLimitWarningOpen: true });
      return;
    }
    this.createTeam(data);
  };

  createTeam = async (data: any) => {
    const { tournament, user } = this.props;
    this.setState({ isAddingTeam: true });
    try {
      await addSchoolTournamentTeam(user, tournament.id, {
        name: data.name,
        gender: data.gender,
        sportId: tournament.sportId,
        schoolId: data.schoolId,
        teamType: TEAM_TYPE.ADHOC,
        players: [],
        ages: [Number(data.age)],
        ...(tournament.maxPlayersPerTeam > 0 ? { numberOfStudents: Number(data.numberOfStudents) } : {})
      });
      this.setState({
        isAddTeamOpen: false,
        isAddingTeam: false,
        isTeamLimitWarningOpen: false,
        pendingTeamData: undefined,
        teamLimitWarnings: []
      });
      this.loadTeams(1, this.state.teamsSearch);
      this.loadUnassignedTeams();
    } catch (error) {
      this.setState({ isAddingTeam: false });
    }
  };

  assignTeamNumbers = async (event: React.FormEvent) => {
    event.preventDefault();
    const startFrom = Number(this.state.assignTeamNumbersFrom);
    if (!Number.isInteger(startFrom) || startFrom < 1) {
      this.setState({ assignTeamNumbersError: 'Enter a starting number of 1 or greater.' });
      return;
    }
    this.setState({ isAssigningTeamNumbers: true, assignTeamNumbersError: '' });
    try {
      await assignSchoolUnionTournamentTeamPlayerNumbersToTournament(
        this.props.user,
        this.props.tournament.schoolId,
        this.props.tournament.id,
        { startFrom, mode: this.state.assignTeamNumbersMode }
      );
      this.setState({ isAssignTeamNumbersOpen: false, isAssigningTeamNumbers: false });
      this.loadTeams(this.state.teamsPage, this.state.teamsSearch);
    } catch (error) {
      this.setState({
        isAssigningTeamNumbers: false,
        assignTeamNumbersError:
          propz.get(error, ['response', 'data', 'details', 'text']) || 'Unable to assign athlete numbers.'
      });
    }
  };

  renderTeams = () => {
    const { teams, teamsPage, teamsTotal, teamsSearch, teamsSearchDraft, isTeamsLoading } = this.state;
    const visible = this.state.visibleTeamColumns;
    const selectedTeams = teams.filter(team => this.state.selectedTeamIds.includes(team.id));
    const columnCount = 2 + visible.length;
    const hasTeamFilters = Boolean(teamsSearch || this.state.teamGenderFilters.length || this.state.teamGroupFilter);
    const hasAnyTeams = (this.state.teamsUnfilteredTotal ?? teamsTotal) > 0;
    return (
      <>
        <article className="utmCompetition__panel utmCompetitionTeams">
          <header>
            <h2>
              Teams <b>{teamsTotal}</b>
            </h2>
            <div className="gridModernSearch utmCompetitionTeams__search">
              <form onSubmit={this.onTeamsSearch}>
                <input
                  value={teamsSearchDraft}
                  onChange={event => this.setState({ teamsSearchDraft: event.target.value })}
                  placeholder="Search by team name"
                />
              </form>
            </div>
            <button
              className={`utmCompetitionTeams__tool ${
                this.state.teamGenderFilters.length || this.state.teamGroupFilter ? 'is-active' : ''
              }`}
              type="button"
              onClick={() =>
                this.setState(state => ({
                  isTeamFiltersOpen: true,
                  teamGenderFilterDraft: [...state.teamGenderFilters],
                  teamGroupFilterDraft: state.teamGroupFilter
                }))
              }
            >
              <Icon path="M4 6h16M7 12h10M10 18h4" /> Filters
              {this.state.teamGenderFilters.length || this.state.teamGroupFilter ? (
                <b>{this.state.teamGenderFilters.length + Number(Boolean(this.state.teamGroupFilter))}</b>
              ) : null}
            </button>
            <button
              className="utmCompetitionTeams__tool"
              type="button"
              onClick={() => this.setState({ isTeamColumnsOpen: true })}
            >
              <Icon path="M5 4h14v16H5zM9 4v16M15 4v16" /> Columns
            </button>
            <button
              className="utmCompetitionTeams__tool"
              type="button"
              onClick={() =>
                this.setState({
                  isAssignTeamNumbersOpen: true,
                  assignTeamNumbersFrom: '1',
                  assignTeamNumbersMode: 'FILL_MISSING',
                  assignTeamNumbersError: ''
                })
              }
            >
              <Icon path="M7 4h10M7 12h10M7 20h10M3 4h.01M3 12h.01M3 20h.01" /> Assign numbers
            </button>
            <button
              className="utmCompetitionTeams__add"
              type="button"
              disabled={this.state.isAddingTeam}
              onClick={this.openAddTeam}
            >
              <span>+</span> {this.state.isAddingTeam ? 'Loading…' : 'Add team'}
            </button>
          </header>
          {this.state.selectedTeamIds.length ? (
            <div className="utmCompetitionTeams__selection">
              <strong>Selected {this.state.selectedTeamIds.length}</strong>
              <button
                className="is-danger"
                type="button"
                onClick={() => this.setState({ isDeleteTeamsOpen: true, deleteTeamsError: '' })}
              >
                Delete
              </button>
              <button
                className="is-clearSelection"
                type="button"
                onClick={() => this.setState({ selectedTeamIds: [] })}
              >
                <span aria-hidden="true">×</span> Clear selection
              </button>
            </div>
          ) : null}
          {teamsSearch ? (
            <div className="utmCompetitionTeams__filters">
              <span>
                Search: {teamsSearch}
                <button
                  type="button"
                  aria-label="Remove team search filter"
                  onClick={() => {
                    this.setState({ teamsSearchDraft: '' });
                    this.loadTeams(1, '');
                  }}
                >
                  ×
                </button>
              </span>
            </div>
          ) : null}
          <div className="utmCompetitionTeams__tableWrap">
            <table>
              <thead>
                <tr>
                  <th className="utmCompetitionTeams__selectColumn" />
                  <th>Team</th>
                  {visible.includes('school') ? <th>School</th> : null}
                  {visible.includes('age') ? <th>Age</th> : null}
                  {visible.includes('gender') ? <th>Gender</th> : null}
                  {visible.includes('groups') ? <th>Groups</th> : null}
                  {visible.includes('players') ? <th>Players</th> : null}
                </tr>
              </thead>
              <tbody>
                {isTeamsLoading ? (
                  <tr>
                    <td colSpan={columnCount} className="utmCompetitionTeams__empty">
                      Loading teams…
                    </td>
                  </tr>
                ) : null}
                {!isTeamsLoading &&
                  teams.map(team => {
                    const groups = this.getGroupNames(team);
                    return (
                      <tr
                        key={team.id}
                        onClick={() =>
                          this.setState({ openedTeam: team, editedTeamName: team.name || '', teamDrawerTab: 'details' })
                        }
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={this.state.selectedTeamIds.includes(team.id)}
                            onClick={event => event.stopPropagation()}
                            onChange={() =>
                              this.setState(state => ({
                                selectedTeamIds: state.selectedTeamIds.includes(team.id)
                                  ? state.selectedTeamIds.filter(id => id !== team.id)
                                  : [...state.selectedTeamIds, team.id]
                              }))
                            }
                          />
                        </td>
                        <td>
                          <strong>{team.name || 'Unnamed team'}</strong>
                        </td>
                        {visible.includes('school') ? (
                          <td>
                            <div className="utmCompetitionTeams__school">
                              <img src={team.school?.pic || TOURNAMENT_SCHOOL_LOGO_FALLBACK} alt="" />
                              <strong>{team.school?.name || '—'}</strong>
                            </div>
                          </td>
                        ) : null}
                        {visible.includes('age') ? <td>{formatTournamentAges(team.ages) || '—'}</td> : null}
                        {visible.includes('gender') ? <td>{getTeamGender(team as any) || '—'}</td> : null}
                        {visible.includes('groups') ? (
                          <td>
                            {groups.length ? (
                              groups.map(group => (
                                <span className="utmCompetitionTeams__group" key={group}>
                                  {group}
                                </span>
                              ))
                            ) : (
                              <span className="utmCompetitionTeams__unassigned">Not assigned</span>
                            )}
                          </td>
                        ) : null}
                        {visible.includes('players') ? (
                          <td>
                            {isIndividualTournament(this.props.tournament)
                              ? team.players?.length ?? 0
                              : team.numberOfStudents ?? team.players?.length ?? 0}
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                {!isTeamsLoading && !teams.length ? (
                  <tr>
                    <td colSpan={columnCount} className="utmCompetitionTeams__empty">
                      <div className="utmCompetitionTeams__emptyState">
                        <span aria-hidden="true">
                          <Icon
                            path={
                              hasTeamFilters && hasAnyTeams
                                ? 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM16 16l4 4'
                                : 'M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M8.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.8'
                            }
                          />
                        </span>
                        <strong>
                          {hasTeamFilters && hasAnyTeams ? 'No teams match your filters' : 'No teams yet'}
                        </strong>
                        <p>
                          {hasTeamFilters && hasAnyTeams
                            ? 'Try adjusting your search or clearing one or more filters.'
                            : 'Add your first team to start building the competition.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <Pagination2
            modern
            currentPage={teamsPage}
            total={teamsTotal}
            limit={20}
            setCurrentPageParams={page => this.loadTeams(page, teamsSearch)}
          />
        </article>
        {this.state.isTeamFiltersOpen ? (
          <div className="utmEligiblePanelBackdrop" onMouseDown={() => this.setState({ isTeamFiltersOpen: false })}>
            <form
              className="utmEligiblePanel"
              onMouseDown={event => event.stopPropagation()}
              onSubmit={event => {
                event.preventDefault();
                this.setState(
                  state => ({
                    teamGenderFilters: [...state.teamGenderFilterDraft],
                    teamGroupFilter: state.teamGroupFilterDraft,
                    isTeamFiltersOpen: false
                  }),
                  () => this.loadTeams(1, teamsSearch)
                );
              }}
            >
              <header>
                <h2>Filters</h2>
                <button type="button" onClick={() => this.setState({ isTeamFiltersOpen: false })}>
                  ×
                </button>
              </header>
              <div className="utmEligiblePanel__body">
                <section>
                  <h3>Gender</h3>
                  <div className="utmEligiblePanel__chips">
                    {['MALE', 'FEMALE', 'MIXED'].map(gender => (
                      <button
                        type="button"
                        key={gender}
                        className={this.state.teamGenderFilterDraft.includes(gender) ? 'is-selected' : ''}
                        onClick={() =>
                          this.setState(state => ({
                            teamGenderFilterDraft: state.teamGenderFilterDraft.includes(gender)
                              ? state.teamGenderFilterDraft.filter(item => item !== gender)
                              : [...state.teamGenderFilterDraft, gender]
                          }))
                        }
                      >
                        {gender === 'MALE' ? 'Boys' : gender === 'FEMALE' ? 'Girls' : 'Mixed'}
                      </button>
                    ))}
                  </div>
                </section>
                <section>
                  <h3>Group</h3>
                  <select
                    value={this.state.teamGroupFilterDraft}
                    onChange={event => this.setState({ teamGroupFilterDraft: event.target.value })}
                  >
                    <option value="">All groups</option>
                    <option value="UNASSIGNED">Not assigned</option>
                    {(this.props.tournament.customGroups || []).map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </section>
              </div>
              <footer>
                <button
                  type="button"
                  onClick={() => this.setState({ teamGenderFilterDraft: [], teamGroupFilterDraft: '' })}
                >
                  Clear all
                </button>
                <button type="button" onClick={() => this.setState({ isTeamFiltersOpen: false })}>
                  Cancel
                </button>
                <button className="is-primary" type="submit">
                  Apply
                </button>
              </footer>
            </form>
          </div>
        ) : null}
        {this.state.isTeamColumnsOpen ? (
          <div className="utmColumnsBackdrop" onMouseDown={() => this.setState({ isTeamColumnsOpen: false })}>
            <div className="utmColumnsModal" onMouseDown={event => event.stopPropagation()}>
              <header>
                <h2>Table columns</h2>
                <button type="button" className="utmColumnsReset" onClick={this.resetTeamColumns}>
                  Reset
                </button>
                <button type="button" onClick={() => this.setState({ isTeamColumnsOpen: false })}>
                  ×
                </button>
              </header>
              <div>
                {[
                  ['school', 'School'],
                  ['age', 'Age'],
                  ['gender', 'Gender'],
                  ['groups', 'Groups'],
                  ['players', 'Players']
                ].map(([key, label]) => (
                  <label key={key}>
                    <input
                      type="checkbox"
                      checked={visible.includes(key)}
                      onChange={() => this.toggleTeamColumn(key)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <footer>
                <button type="button" onClick={() => this.setState({ isTeamColumnsOpen: false })}>
                  Done
                </button>
              </footer>
            </div>
          </div>
        ) : null}
        <SimpleModal
          isOpen={this.state.isAssignTeamNumbersOpen}
          title="Assign athlete numbers"
          customClass="utmTeamNumbersModal"
          dialogStyle={{ maxWidth: 440 }}
          showFooter={false}
          isBodyBold={false}
          onCloseClick={() =>
            !this.state.isAssigningTeamNumbers &&
            this.setState({ isAssignTeamNumbersOpen: false, assignTeamNumbersError: '' })
          }
        >
          <form className="utmTeamNumbersForm" onSubmit={this.assignTeamNumbers}>
            <p>Assign unique athlete numbers across every team in this tournament.</p>
            <label>
              <span>
                Starting number <b>*</b>
              </span>
              <input
                type="number"
                min="1"
                required
                autoFocus
                value={this.state.assignTeamNumbersFrom}
                onChange={event => this.setState({ assignTeamNumbersFrom: event.target.value })}
              />
            </label>
            <fieldset>
              <legend>Assignment mode</legend>
              <label className="utmTeamNumbersForm__option">
                <input
                  type="radio"
                  name="teamNumbersMode"
                  checked={this.state.assignTeamNumbersMode === 'FILL_MISSING'}
                  onChange={() => this.setState({ assignTeamNumbersMode: 'FILL_MISSING' })}
                />
                <span>
                  <strong>Fill missing numbers only</strong>
                  <small>Keep all existing athlete numbers unchanged.</small>
                </span>
              </label>
              <label className="utmTeamNumbersForm__option">
                <input
                  type="radio"
                  name="teamNumbersMode"
                  checked={this.state.assignTeamNumbersMode === 'RENUMBER_ALL'}
                  onChange={() => this.setState({ assignTeamNumbersMode: 'RENUMBER_ALL' })}
                />
                <span>
                  <strong>Renumber all athletes</strong>
                  <small>Replace every existing athlete number.</small>
                </span>
              </label>
            </fieldset>
            {this.state.assignTeamNumbersError ? (
              <div className="utmTeamNumbersForm__error">{this.state.assignTeamNumbersError}</div>
            ) : null}
            <footer>
              <button
                type="button"
                className="is-secondary"
                disabled={this.state.isAssigningTeamNumbers}
                onClick={() => this.setState({ isAssignTeamNumbersOpen: false, assignTeamNumbersError: '' })}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="is-primary"
                disabled={this.state.isAssigningTeamNumbers || !this.state.assignTeamNumbersFrom}
              >
                {this.state.isAssigningTeamNumbers ? 'Assigning…' : 'Assign numbers'}
              </button>
            </footer>
          </form>
        </SimpleModal>
        {this.state.openedTeam ? (
          <div
            className="utmConfirmationDrawer utmTeamDrawer"
            onMouseDown={event => event.target === event.currentTarget && this.setState({ openedTeam: undefined })}
          >
            <aside>
              <header>
                <div>
                  <h3>{this.state.openedTeam.name}</h3>
                  <p>{this.state.openedTeam.school?.name || 'Tournament team'}</p>
                </div>
                <button type="button" onClick={() => this.setState({ openedTeam: undefined })}>
                  ×
                </button>
              </header>
              <nav>
                <button
                  type="button"
                  className={this.state.teamDrawerTab === 'details' ? 'is-active' : ''}
                  onClick={() => this.setState({ teamDrawerTab: 'details' })}
                >
                  Details
                </button>
                <button
                  type="button"
                  className={this.state.teamDrawerTab === 'members' ? 'is-active' : ''}
                  onClick={() => this.setState({ teamDrawerTab: 'members' })}
                >
                  Team members
                </button>
              </nav>
              <div className="utmConfirmationDrawer__body">
                {this.state.teamDrawerTab === 'details' ? (
                  <>
                    <label className="utmTeamDrawer__name">
                      Team name
                      <input
                        value={this.state.editedTeamName}
                        onChange={event => this.setState({ editedTeamName: event.target.value })}
                      />
                    </label>
                    <dl>
                      <div>
                        <dt>School</dt>
                        <dd>{this.state.openedTeam.school?.name || '—'}</dd>
                      </div>
                      <div>
                        <dt>Groups</dt>
                        <dd>{this.getGroupNames(this.state.openedTeam).join(', ') || 'Not assigned'}</dd>
                      </div>
                      <div>
                        <dt>Players</dt>
                        <dd>{this.state.openedTeam.players?.length || 0}</dd>
                      </div>
                    </dl>
                  </>
                ) : (
                  <div className="utmTeamDrawer__members">
                    <strong>{this.state.openedTeam.players?.length || 0} team members</strong>
                    <p>
                      Manage players, assign participant numbers and edit student details in the dedicated two-panel
                      workspace.
                    </p>
                    <button type="button" onClick={() => this.props.onManageTeam(this.state.openedTeam!)}>
                      Manage players
                    </button>
                  </div>
                )}
              </div>
              <footer>
                {this.state.teamDrawerTab === 'details' ? (
                  <>
                    <button
                      className="is-secondary"
                      type="button"
                      onClick={() => this.setState({ openedTeam: undefined })}
                    >
                      Cancel
                    </button>
                    <button
                      className="is-primary"
                      type="button"
                      disabled={this.state.isSavingTeam || !this.state.editedTeamName.trim()}
                      onClick={this.saveTeamName}
                    >
                      {this.state.isSavingTeam ? 'Saving…' : 'Save changes'}
                    </button>
                  </>
                ) : (
                  <button
                    className="is-secondary"
                    type="button"
                    onClick={() => this.setState({ openedTeam: undefined })}
                  >
                    Close
                  </button>
                )}
              </footer>
            </aside>
          </div>
        ) : null}
        <SimpleModal isOpen={this.state.isDeleteTeamsOpen} isBare onBackdropClick={this.closeDeleteTeamsModal}>
          <section
            className="utmTeamsDeleteDialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-teams-title"
          >
            <header>
              <div>
                <h2 id="delete-teams-title">
                  {this.state.deleteTeamsError ? 'Teams cannot be deleted' : 'Delete selected teams?'}
                </h2>
                <p>
                  {this.state.deleteTeamsError
                    ? 'A participant must retain at least one team.'
                    : `${selectedTeams.length} team${selectedTeams.length === 1 ? '' : 's'} will be removed.`}
                </p>
              </div>
              <button
                type="button"
                aria-label="Close"
                disabled={this.state.isDeletingTeams}
                onClick={this.closeDeleteTeamsModal}
              >
                ×
              </button>
            </header>
            <div className="utmTeamsDeleteDialog__body">
              <div className="utmTeamsDeleteDialog__teams">
                {selectedTeams.map(team => (
                  <span key={team.id}>{team.name}</span>
                ))}
              </div>
              <p
                className={
                  this.state.deleteTeamsError ? 'utmTeamsDeleteDialog__blocked' : 'utmTeamsDeleteDialog__warning'
                }
              >
                {this.state.deleteTeamsError || 'This action cannot be undone.'}
              </p>
            </div>
            <footer>
              <button type="button" disabled={this.state.isDeletingTeams} onClick={this.closeDeleteTeamsModal}>
                {this.state.deleteTeamsError ? 'Close' : 'Cancel'}
              </button>
              {!this.state.deleteTeamsError ? (
                <button
                  type="button"
                  className="is-danger"
                  disabled={this.state.isDeletingTeams}
                  onClick={this.deleteSelectedTeams}
                >
                  {this.state.isDeletingTeams ? 'Checking…' : 'Delete teams'}
                </button>
              ) : null}
            </footer>
          </section>
        </SimpleModal>
        <SimpleModal
          isOpen={this.state.isAddTeamOpen}
          title="Add team"
          customClass="utmTeamEditorModal"
          dialogStyle={{ maxWidth: 460 }}
          showFooter={false}
          onCloseClick={() => this.setState({ isAddTeamOpen: false })}
        >
          <AdminSUTournamentTeamForm
            tournament={this.props.tournament}
            user={this.props.user}
            allTournamentTeams={this.state.allTournamentTeams}
            result={undefined}
            onSubmit={this.addTeam}
            onCancel={() => this.setState({ isAddTeamOpen: false })}
          />
        </SimpleModal>
        <SimpleModal
          isOpen={this.state.isTeamLimitWarningOpen}
          isBare
          onBackdropClick={() =>
            !this.state.isAddingTeam &&
            this.setState({ isTeamLimitWarningOpen: false, pendingTeamData: undefined, teamLimitWarnings: [] })
          }
        >
          <section className="utmTeamLimitWarning" role="alertdialog" aria-modal="true">
            <header>
              <div>
                <h2>Team limits will be exceeded</h2>
                <p>The organiser can override these limits for this team.</p>
              </div>
              <button
                type="button"
                aria-label="Close"
                disabled={this.state.isAddingTeam}
                onClick={() =>
                  this.setState({ isTeamLimitWarningOpen: false, pendingTeamData: undefined, teamLimitWarnings: [] })
                }
              >
                ×
              </button>
            </header>
            <div>
              {this.state.teamLimitWarnings.map(message => (
                <p key={message}>{message}</p>
              ))}
              <small>This exception applies only to a union administrator organising this tournament.</small>
            </div>
            <footer>
              <button
                type="button"
                disabled={this.state.isAddingTeam}
                onClick={() =>
                  this.setState({ isTeamLimitWarningOpen: false, pendingTeamData: undefined, teamLimitWarnings: [] })
                }
              >
                Go back
              </button>
              <button
                type="button"
                className="is-warning"
                disabled={this.state.isAddingTeam}
                onClick={() => this.state.pendingTeamData && this.createTeam(this.state.pendingTeamData)}
              >
                {this.state.isAddingTeam ? 'Adding…' : 'Add anyway'}
              </button>
            </footer>
          </section>
        </SimpleModal>
      </>
    );
  };

  getGroupStage = (group: TournamentCustomGroup) => {
    const stage = (this.props.tournament.tournamentLayout || []).find(item => item.id === group.stageId);
    const type = String(stage?.type || 'GROUP').toUpperCase();
    const label = stage?.name || `${type.charAt(0)}${type.slice(1).toLowerCase()}`;
    return { type, label };
  };

  getGroupPitch = (group: TournamentCustomGroup) =>
    (this.state.pitches || []).find(pitch => pitch.id === group.pitchId)?.name || 'Not set';

  saveDefaultGroups = async (data: any) => {
    if (this.state.isSavingDefaultGroups) return;
    const { tournament, user } = this.props;
    const currentGroups = this.state.groups;
    const stageTypeForName = (name: string) => {
      if (TOURNAMENT_GROUP_STAGE.includes(name as any)) return TOURNAMENT_LAYOUT_TYPE.GROUP;
      if (TOURNAMENT_QUALIFIER_STAGE.includes(name as any)) return TOURNAMENT_LAYOUT_TYPE.QUALIFIER;
      if (TOURNAMENT_PLAYOFF_STAGE.includes(name as any)) return TOURNAMENT_LAYOUT_TYPE.PLAYOFF;
      if (TOURNAMENT_FINAL_STAGE.includes(name as any)) return TOURNAMENT_LAYOUT_TYPE.FINAL;
      return undefined;
    };
    const operations: Promise<any>[] = [];
    Object.keys(data).forEach(key => {
      const name = (TOURNAMENT_GROUP_TAG_MAPPING as any)[key];
      if (!name) return;
      const existing = currentGroups.find(group => group.name === name);
      if (data[key] === false && existing) {
        operations.push(deleteTournamentGroup(user, tournament.id, existing.id));
      }
      if (data[key] === true && !existing) {
        const stageType = stageTypeForName(name);
        const stage = (tournament.tournamentLayout || []).find(item => item.type === stageType);
        if (stage) operations.push(createTournamentGroup(user, tournament.id, { name, stageId: stage.id }));
      }
    });
    this.setState({ isSavingDefaultGroups: true });
    try {
      await Promise.all(operations);
      this.setState({ isDefaultGroupsOpen: false, isSavingDefaultGroups: false, selectedGroupIds: [] });
      this.loadGroups();
      this.loadUnassignedTeams();
    } catch (error) {
      this.setState({ isSavingDefaultGroups: false });
    }
  };

  openGroupDrawer = (group: TournamentCustomGroup) => {
    this.setState({
      openedGroup: group,
      editedGroupName: group.name || '',
      editedGroupStageId: group.stageId || '',
      groupFormError: ''
    });
  };

  hasUnsavedGroupChanges = () => {
    const { openedGroup, editedGroupName, editedGroupStageId } = this.state;
    return Boolean(
      openedGroup &&
        (editedGroupName.trim() !== (openedGroup.name || '').trim() || editedGroupStageId !== openedGroup.stageId)
    );
  };

  requestCloseGroupDrawer = () => {
    if (this.state.isSavingGroup) return;
    if (this.hasUnsavedGroupChanges()) {
      this.setState({ isDiscardGroupChangesOpen: true });
      return;
    }
    this.setState({ openedGroup: undefined, groupFormError: '' });
  };

  discardGroupChanges = () => {
    this.setState({ openedGroup: undefined, isDiscardGroupChangesOpen: false, groupFormError: '' });
  };

  saveGroup = async () => {
    const { openedGroup, editedGroupName, editedGroupStageId } = this.state;
    const name = editedGroupName.trim();
    if (!openedGroup || this.state.isSavingGroup) return;
    if (!name || !editedGroupStageId) {
      this.setState({ groupFormError: 'Enter a group name and select a level.' });
      return;
    }

    this.setState({ isSavingGroup: true, groupFormError: '' });
    try {
      const updatedGroup = await editTournamentGroup(this.props.user, this.props.tournament.id, openedGroup.id, {
        name,
        stageId: editedGroupStageId
      });
      this.setState(state => ({
        groups: state.groups.map(group => (group.id === openedGroup.id ? updatedGroup : group)),
        openedGroup: undefined,
        isSavingGroup: false
      }));
    } catch (error) {
      this.setState({ isSavingGroup: false, groupFormError: 'The group could not be saved. Please try again.' });
    }
  };

  openAddCustomGroup = () => {
    const firstLevel = (this.props.tournament.tournamentLayout || [])[0];
    this.setState({
      isAddCustomGroupOpen: true,
      newGroupName: '',
      newGroupStageId: firstLevel ? firstLevel.id : '',
      groupFormError: ''
    });
  };

  createCustomGroup = async () => {
    const name = this.state.newGroupName.trim();
    const stageId = this.state.newGroupStageId;
    if (this.state.isCreatingCustomGroup) return;
    if (!name || !stageId) {
      this.setState({ groupFormError: 'Enter a group name and select a level.' });
      return;
    }

    this.setState({ isCreatingCustomGroup: true, groupFormError: '' });
    try {
      await createTournamentGroup(this.props.user, this.props.tournament.id, { name, stageId });
      this.setState({
        isAddCustomGroupOpen: false,
        isCreatingCustomGroup: false,
        newGroupName: '',
        newGroupStageId: ''
      });
      this.loadGroups();
    } catch (error) {
      this.setState({
        isCreatingCustomGroup: false,
        groupFormError: 'The group could not be created. Please try again.'
      });
    }
  };

  deleteSelectedGroups = async () => {
    if (this.state.isDeletingGroups) return;
    this.setState({ isDeletingGroups: true, deleteGroupsError: '' });
    try {
      const checks = await Promise.all(
        this.state.selectedGroupIds.map(async groupId => ({
          groupId,
          check: await getTournamentGroupDeletionCheck(this.props.user, this.props.tournament.id, groupId)
        }))
      );
      const blocked = checks.filter(item => !item.check.canDelete);
      if (blocked.length) {
        const details = blocked.map(item => {
          const group = this.state.groups.find(candidate => candidate.id === item.groupId);
          const links: string[] = [];
          if (item.check.teamsCount) {
            links.push(`${item.check.teamsCount} team${item.check.teamsCount === 1 ? '' : 's'}`);
          }
          if (item.check.eventsCount) {
            links.push(`${item.check.eventsCount} event${item.check.eventsCount === 1 ? '' : 's'}`);
          }
          return `${group?.name || 'Unnamed group'} (${links.join(' and ')})`;
        });
        this.setState({
          isDeletingGroups: false,
          deleteGroupsError: `${
            blocked.length === 1 ? 'This group cannot' : 'These groups cannot'
          } be deleted: ${details.join(
            ', '
          )}. Unassign the linked teams and remove or reassign the linked tournament events first.`
        });
        return;
      }

      await Promise.all(
        this.state.selectedGroupIds.map(groupId =>
          deleteTournamentGroup(this.props.user, this.props.tournament.id, groupId)
        )
      );
      this.setState({ selectedGroupIds: [], isDeleteGroupsOpen: false, isDeletingGroups: false });
      this.loadGroups();
      this.loadUnassignedTeams();
    } catch (error) {
      const message = (error as any)?.response?.data?.message;
      this.setState({
        isDeletingGroups: false,
        deleteGroupsError: message || 'The selected groups could not be deleted. Please try again.'
      });
    }
  };

  loadPitches = async (page: number, search: string = this.state.pitchesSearch) => {
    if (!this.isTeamCompetition()) return;
    this.setState({ isPitchesLoading: true });
    try {
      const where = search ? { name: { like: search, options: 'i' } } : undefined;
      const [pitches, countResult] = await Promise.all([
        getTournamentPitches(
          this.props.user,
          this.props.tournament.id,
          {
            where,
            limit: 20,
            skip: (page - 1) * 20
          },
          search
        ),
        getTournamentPitchesCount(this.props.user, this.props.tournament.id, where, search)
      ]);
      const total = Number((countResult as any)?.count ?? countResult ?? 0);
      this.setState({
        pitches: pitches || [],
        pitchesPage: page,
        pitchesTotal: isFinite(total) ? total : 0,
        pitchesUnfilteredTotal: search ? this.state.pitchesUnfilteredTotal : isFinite(total) ? total : 0,
        pitchesSearch: search,
        pitchesSearchDraft: search,
        isPitchesLoading: false
      });
    } catch (error) {
      this.setState({ pitches: [], pitchesTotal: 0, isPitchesLoading: false });
    }
  };

  openAddPitch = () =>
    this.setState({
      isAddPitchOpen: true,
      pitchName: '',
      pitchSurface: '',
      pitchSize: '',
      pitchNote: '',
      pitchFormError: ''
    });

  openPitchDrawer = (pitch: TournamentPitch) =>
    this.setState({
      openedPitch: pitch,
      pitchName: pitch.name || '',
      pitchSurface: pitch.surface || '',
      pitchSize: pitch.size || '',
      pitchNote: pitch.note || '',
      pitchFormError: ''
    });

  getPitchPayload = () => ({
    name: this.state.pitchName.trim(),
    surface: this.state.pitchSurface.trim(),
    size: this.state.pitchSize.trim(),
    note: this.state.pitchNote.trim()
  });

  hasUnsavedPitchChanges = () => {
    const pitch = this.state.openedPitch;
    const values = this.getPitchPayload();
    return Boolean(
      pitch &&
        (values.name !== (pitch.name || '').trim() ||
          values.surface !== (pitch.surface || '').trim() ||
          values.size !== (pitch.size || '').trim() ||
          values.note !== (pitch.note || '').trim())
    );
  };

  requestClosePitchDrawer = () => {
    if (this.state.isSavingPitch) return;
    if (this.hasUnsavedPitchChanges()) this.setState({ isDiscardPitchChangesOpen: true });
    else this.setState({ openedPitch: undefined, pitchFormError: '' });
  };

  savePitch = async (isNew: boolean) => {
    if (this.state.isSavingPitch) return;
    const payload = this.getPitchPayload();
    if (!payload.name) {
      this.setState({ pitchFormError: 'Enter a pitch name.' });
      return;
    }
    this.setState({ isSavingPitch: true, pitchFormError: '' });
    try {
      if (isNew) {
        await createPitch(this.props.user, this.props.tournament.id, payload);
      } else if (this.state.openedPitch) {
        await updatePitch(this.props.user, this.props.tournament.id, this.state.openedPitch.id, payload);
      }
      this.setState({ isSavingPitch: false, isAddPitchOpen: false, openedPitch: undefined });
      this.loadPitches(isNew ? 1 : this.state.pitchesPage, this.state.pitchesSearch);
    } catch (error) {
      this.setState({ isSavingPitch: false, pitchFormError: 'The pitch could not be saved. Please try again.' });
    }
  };

  deleteSelectedPitches = async () => {
    if (this.state.isDeletingPitches) return;
    this.setState({ isDeletingPitches: true });
    try {
      await Promise.all(
        this.state.selectedPitchIds.map(pitchId => deletePitch(this.props.user, this.props.tournament.id, pitchId))
      );
      const remaining = Math.max(0, this.state.pitchesTotal - this.state.selectedPitchIds.length);
      const lastPage = Math.max(1, Math.ceil(remaining / 20));
      this.setState({ selectedPitchIds: [], isDeletePitchesOpen: false, isDeletingPitches: false });
      this.loadPitches(Math.min(this.state.pitchesPage, lastPage), this.state.pitchesSearch);
    } catch (error) {
      this.setState({ isDeletingPitches: false });
    }
  };

  getEventsWhere = () => {
    const where: any = {};
    const isTeam = this.isTeamCompetition();
    if (this.state.eventSportFilter.length) where.sportId = { $in: this.state.eventSportFilter };
    if (this.state.eventAgeFilter.length) where.ages = { $in: this.state.eventAgeFilter.map(Number) };
    if (this.state.eventGenderFilter.length) where.gender = { $in: this.state.eventGenderFilter };
    if (isTeam && this.state.eventGroupFilter.length) {
      where.$and = [
        ...(where.$and || []),
        { tournamentGroupId: { $in: this.state.eventGroupFilter } }
      ];
    }
    if (isTeam && this.state.eventStageFilter.length) {
      const definition = this.getCompetitionDefinition();
      const groupStageIds = new Set(
        (this.props.tournament.tournamentLayout || [])
          .filter(stage => stage.type === TOURNAMENT_LAYOUT_TYPE.GROUP)
          .map(stage => String(stage.id))
      );
      const stageConditions: any[] = [];
      if (this.state.eventStageFilter.includes(GROUP_STAGE_FILTER)) {
        const groupIds = (this.props.tournament.customGroups || [])
          .filter(group => groupStageIds.has(String(group.stageId)))
          .map(group => group.id);
        if (groupIds.length) stageConditions.push({ tournamentGroupId: { $in: groupIds } });
      }
      const roundIds = this.state.eventStageFilter.filter(value => value !== GROUP_STAGE_FILTER);
      const roundMatches = (definition?.matches || []).filter(match => roundIds.includes(this.getStructureMatchRound(match)));
      const eventIds = roundMatches.map(match => match.eventId).filter(Boolean);
      const structureMatchIds = roundMatches.map(match => match.id).filter(Boolean);
      if (eventIds.length) stageConditions.push({ _id: { $in: eventIds } });
      if (structureMatchIds.length) {
        stageConditions.push({ tournamentStructureMatchId: { $in: structureMatchIds } });
      }
      where.$and = [...(where.$and || []), stageConditions.length ? { $or: stageConditions } : { _id: { $in: [] } }];
    }

    const selectedPitchIds = isTeam ? this.state.eventPitchFilter.filter(pitchId => pitchId !== 'UNASSIGNED') : [];
    const includesUnassignedPitch = isTeam && this.state.eventPitchFilter.includes('UNASSIGNED');
    if (selectedPitchIds.length && includesUnassignedPitch) {
      where.$or = [{ pitchId: { $in: selectedPitchIds } }, { pitchId: { $exists: false } }];
    } else if (selectedPitchIds.length) {
      where.pitchId = { $in: selectedPitchIds };
    } else if (includesUnassignedPitch) {
      where.pitchId = { $exists: false };
    }
    const statuses = this.state.eventStatusFilter.filter(status => status !== 'NOT_STARTED');
    const includesNotStarted = this.state.eventStatusFilter.includes('NOT_STARTED');
    if (statuses.length && includesNotStarted) {
      where.$and = [...(where.$and || []), { $or: [{ status: { $in: statuses } }, { status: { $exists: false } }] }];
    } else if (statuses.length) {
      where.status = { $in: statuses };
    } else if (includesNotStarted) {
      where.status = { $exists: false };
    }
    return where;
  };

  loadEvents = async (page: number) => {
    const where = this.getEventsWhere();
    const isFiltered = Object.keys(where).length > 0;
    this.setState({ isEventsLoading: true });
    try {
      const [events, countResult] = await Promise.all([
        getTournamentEvents(
          this.props.user,
          this.props.tournament.id,
          {
            where,
            limit: 20,
            skip: (page - 1) * 20,
            order: 'startTime ASC'
          },
          { summary: true }
        ),
        getTournamentEventsCount(this.props.user, this.props.tournament.id, where)
      ]);
      const total = Number((countResult as any)?.count ?? countResult ?? 0);
      this.setState({
        events: events || [],
        eventsPage: page,
        eventsTotal: isFinite(total) ? total : 0,
        eventsUnfilteredTotal: isFiltered ? this.state.eventsUnfilteredTotal : isFinite(total) ? total : 0,
        isEventsLoading: false
      });
    } catch (error) {
      this.setState({ events: [], eventsTotal: 0, isEventsLoading: false });
    }
  };

  getTournamentEventName = (event: SchoolEvent) => {
    const generatedNames: any = event.generatedNames || {};
    const generated =
      generatedNames[this.props.user.activeSchoolId] ||
      event.eventDetails?.shortDescription ||
      generatedNames.official ||
      Object.keys(generatedNames)
        .map(key => generatedNames[key])
        .find(Boolean);
    if (generated) return String(generated);
    const teams = (event.embeddedTeams || []).map(team => team.name).filter(Boolean);
    if (teams.length > 1) return `${teams[0]} vs ${teams[1]}`;
    return teams[0] || event.sport?.name || 'Tournament event';
  };

  getCompetitionDefinition = () =>
    this.props.tournament.competitionStructure?.published || this.props.tournament.competitionStructure?.draft;

  getStructureMatchRound = (match: any): string => {
    if (match.round) return String(match.round);
    const group = (this.props.tournament.customGroups || []).find(item => String(item.id) === String(match.groupId));
    const stage = (this.props.tournament.tournamentLayout || []).find(item => String(item.id) === String(group?.stageId));
    return ({ QUALIFIER: 'QUARTER_FINAL', PLAYOFF: 'SEMI_FINAL', FINAL: 'FINAL' } as any)[String(stage?.type)] || 'PLAYOFF';
  };

  getEventStageOptions = () => {
    const definition = this.getCompetitionDefinition();
    const usedRounds = new Set((definition?.matches || []).map(match => this.getStructureMatchRound(match)));
    const hasGroupStage = (this.props.tournament.tournamentLayout || []).some(
      stage => stage.type === TOURNAMENT_LAYOUT_TYPE.GROUP
    );
    return [
      ...(hasGroupStage ? [{ id: GROUP_STAGE_FILTER, name: 'Group stage' }] : []),
      ...KNOCKOUT_ROUND_OPTIONS.filter(round => usedRounds.has(round.id))
    ];
  };

  getTournamentEventStage = (event: SchoolEvent) => {
    const group = (this.props.tournament.customGroups || []).find(
      item => String(item.id) === String(event.tournamentGroupId)
    );
    const definition = this.getCompetitionDefinition();
    const match = (definition?.matches || []).find(
      item =>
        String(item.eventId || '') === String(event.id) ||
        (event.tournamentStructureMatchId && String(item.id) === String(event.tournamentStructureMatchId))
    );
    if (match) {
      const roundId = this.getStructureMatchRound(match);
      return {
        competition: group?.name || 'Knockout',
        round: KNOCKOUT_ROUND_OPTIONS.find(item => item.id === roundId)?.name || roundId.replace(/_/g, ' ')
      };
    }
    const stage = (this.props.tournament.tournamentLayout || []).find(
      item => String(item.id) === String(group?.stageId)
    );
    return {
      competition: group?.name || stage?.name || '',
      round: stage?.type === TOURNAMENT_LAYOUT_TYPE.GROUP ? 'Group stage' : stage?.name || String(stage?.type || '')
    };
  };

  getTournamentEventPitch = (event: SchoolEvent) =>
    (this.state.pitches || []).find(pitch => String(pitch.id) === String(event.pitchId))?.name || '';

  getTournamentEventScore = (event: SchoolEvent) => {
    const scores = event.results?.teamScore || [];
    if (scores.length < 2) return '';
    const readScore = (score: any) =>
      score.richScore && typeof score.richScore.result !== 'undefined' ? score.richScore.result : score.score;
    return `${readScore(scores[0])} – ${readScore(scores[1])}`;
  };

  getEventParticipantLimit = (event: SchoolEvent | Sport): number =>
    Number(propz.get(event, ['sport', 'defaultLimits', 'maxPlayers'], 0)) ||
    Number(propz.get(event, ['defaultLimits', 'maxPlayers'], 0));

  shouldShowEventParticipantLimit = (events: Array<SchoolEvent | Sport> = this.state.events): boolean =>
    this.props.tournament.isEventParticipantLimitEnforced !== false &&
    events.some(event => this.getEventParticipantLimit(event) > 0);

  getEventListState = (): TournamentEventListState => ({
    page: this.state.eventsPage,
    sportFilters: [...this.state.eventSportFilter],
    ageFilters: [...this.state.eventAgeFilter],
    genderFilters: [...this.state.eventGenderFilter],
    groupFilters: [...this.state.eventGroupFilter],
    pitchFilters: [...this.state.eventPitchFilter],
    stageFilters: [...this.state.eventStageFilter],
    statusFilters: [...this.state.eventStatusFilter]
  });

  exportEventsCsv = async () => {
    const events: SchoolEvent[] = await getAllTournamentEvents(
      this.props.user,
      this.props.tournament.id,
      this.getEventsWhere()
    );
    const isTeam = this.isTeamCompetition();
    const showParticipantLimit = !isTeam && this.shouldShowEventParticipantLimit(events || []);
    const headings = isTeam
      ? ['Time', 'Competition', 'Round', 'Pitch', 'Fixture', 'Score', 'Status']
      : ['Date and time', 'Sport', 'Event', 'Age', 'Gender', 'Athletes', ...(showParticipantLimit ? ['Max per school'] : []), 'Status'];
    const rows = (events || []).map(event =>
      isTeam
        ? [
            new Date(event.startTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
            this.getTournamentEventStage(event).competition,
            this.getTournamentEventStage(event).round,
            this.getTournamentEventPitch(event),
            this.getTournamentEventName(event),
            this.getTournamentEventScore(event),
            getEventStatus(event)
          ]
        : [
            new Date(event.startTime).toLocaleString('en-GB'),
            event.sport?.name || '',
            this.getTournamentEventName(event),
            formatTournamentAges(event.ages),
            getEventGender(event),
            getUniqueEventAthleteCount(event),
            ...(showParticipantLimit ? [this.getEventParticipantLimit(event) || ''] : []),
            getEventStatus(event)
          ]
    );
    const escapeCell = (value: any) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = [headings, ...rows].map(row => row.map(escapeCell).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${isTeam ? 'fixtures' : 'events'}-${this.props.tournament.id}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  deleteSelectedEvents = async () => {
    if (this.state.isDeletingEvents) return;
    this.setState({ isDeletingEvents: true });
    try {
      await Promise.all(this.state.selectedEventIds.map(eventId => deleteEvent(this.props.user, eventId)));
      const remaining = Math.max(0, this.state.eventsTotal - this.state.selectedEventIds.length);
      const lastPage = Math.max(1, Math.ceil(remaining / 20));
      this.setState({ selectedEventIds: [], isDeleteEventsOpen: false, isDeletingEvents: false });
      this.loadEvents(Math.min(this.state.eventsPage, lastPage));
    } catch (error) {
      this.setState({ isDeletingEvents: false });
    }
  };

  assignPitchToSelectedEvents = async () => {
    if (this.state.isAssigningEventPitch) return;
    this.setState({ isAssigningEventPitch: true });
    try {
      await Promise.all(
        this.state.selectedEventIds.map(eventId =>
          this.state.selectedEventPitchId
            ? assignPitchByTournamentIdAndEventId(
                this.props.user,
                { pitchId: this.state.selectedEventPitchId },
                this.props.tournament.id,
                eventId
              )
            : unassignPitchByTournamentIdAndEventId(this.props.user, this.props.tournament.id, eventId)
        )
      );
      this.setState({ isAssignEventPitchOpen: false, isAssigningEventPitch: false, selectedEventPitchId: '' });
      this.loadEvents(this.state.eventsPage);
    } catch (error) {
      this.setState({ isAssigningEventPitch: false });
    }
  };

  openAddFixtureModal = async () => {
    this.setState({ isFixtureActionLoading: true, fixtureActionError: '', fixtureActionSuccess: '' });
    try {
      const fixtureParticipants = await getAllTournamentParticipants(this.props.user, this.props.tournament.id);
      this.setState({
        isAddFixtureOpen: true,
        fixtureParticipants,
        isFixtureActionLoading: false,
        isFixtureDirty: false,
        isDiscardFixtureChangesOpen: false
      });
    } catch (error) {
      this.setState({ isFixtureActionLoading: false, fixtureActionError: 'Unable to load tournament participants.' });
    }
  };

  requestCloseFixture = () => {
    if (this.state.isFixtureDirty) {
      this.setState({ isDiscardFixtureChangesOpen: true });
      return;
    }
    this.setState({ isAddFixtureOpen: false, fixtureActionError: '', isFixtureDirty: false });
  };

  handleFixtureDirtyChange = (isFixtureDirty: boolean) => {
    if (this.state.isFixtureDirty !== isFixtureDirty) this.setState({ isFixtureDirty });
  };

  discardFixtureChanges = () => {
    this.setState({
      isAddFixtureOpen: false,
      isDiscardFixtureChangesOpen: false,
      isFixtureDirty: false,
      fixtureActionError: ''
    });
  };

  createFixture = async (data: any) => {
    if (this.isCreatingFixture) return;
    this.isCreatingFixture = true;
    const { user, tournament } = this.props;
    const { activeSchoolId } = user;
    const {
      gender,
      ages,
      sportId,
      start,
      end,
      groupId,
      venue,
      teams = [],
      participants = [],
      shortDescription,
      pitchId,
      individualEventFormat
    } = data;
    const isTeamEvent = this.isTeamCompetition();
    const body: any = {
      gender,
      eventType: 'EXTERNAL_SCHOOLS',
      ages: ages.map(age => Number(age)),
      sportId,
      startTime: start.toISOString(),
      endTime: end.toISOString(),
      inviterSchoolId: activeSchoolId,
      tournamentId: tournament.id,
      tournamentGroupId: groupId,
      shortDescription: shortDescription || '',
      ...(!isTeamEvent && individualEventFormat ? { individualEventFormat } : {}),
      venue: {
        venueType: venue.venueType,
        point: venue.venueType === EVENT_VENUE.TBD ? DEFAULT_POINT : venue.point
      },
      invitedSchoolIds: Lazy(isTeamEvent ? teams : participants)
        .map((item: any) => item.schoolId)
        .uniq()
        .toArray()
    };
    const postcodeId = propz.get(venue, ['postcodeData', 'id']);
    const placeId = propz.get(venue, ['postcodeData', 'placeId']);
    if (postcodeId) body.venue.postcodeId = postcodeId;
    if (placeId) body.venue.placeId = placeId;

    this.setState({ isFixtureActionLoading: true, fixtureActionError: '' });
    let createdEventId: string | undefined;
    let isEventActivated = false;
    try {
      const createdEvent = await createEvent(user, body);
      createdEventId = createdEvent.id;
      if (pitchId) {
        await assignPitchByTournamentIdAndEventId(user, { pitchId }, tournament.id, createdEvent.id);
      }
      if (isTeamEvent) {
        await Promise.all(
          teams.map(team =>
            addTeam(user, tournament.id, createdEvent.id, {
              ...team,
              cloneOf: team.id,
              teamType: TEAM_TYPE.CLONE,
              players: isMiniTeamTournament(tournament) ? [] : team.players
            })
          )
        );
      }
      await activateEvent(user, createdEvent.id);
      isEventActivated = true;
      this.setState({
        isAddFixtureOpen: false,
        isFixtureDirty: false,
        isFixtureActionLoading: false,
        fixtureActionSuccess: 'Fixture added successfully.'
      });
      this.loadEvents(1);
    } catch (error) {
      if (createdEventId && !isEventActivated) {
        await deleteEvent(user, createdEventId).catch(cleanupError => {
          console.error('[TournamentCompetition] failed to remove incomplete draft fixture', cleanupError);
        });
      }
      this.setState({
        isFixtureActionLoading: false,
        fixtureActionError: propz.get(error, ['response', 'data', 'details', 'text'], 'Unable to add fixture.')
      });
    } finally {
      this.isCreatingFixture = false;
    }
  };

  createEventsForGroup = async (data: any) => {
    if (this.state.isFixtureActionLoading) return;
    this.setState({ isFixtureActionLoading: true, fixtureActionError: '' });
    try {
      const existing = await getTournamentEventsCount(this.props.user, this.props.tournament.id, {
        tournamentGroupId: data.tournamentGroupId
      });
      if (existing.count > 0) {
        this.setState({
          isFixtureActionLoading: false,
          isReplaceGroupEventsOpen: true,
          pendingGroupEventsData: data,
          replaceGroupEventsCount: existing.count
        });
        return;
      }
      await this.submitGroupEvents(data);
    } catch (error) {
      this.setState({
        isFixtureActionLoading: false,
        fixtureActionError: propz.get(
          error,
          ['response', 'data', 'details', 'text'],
          'Unable to check existing group fixtures.'
        )
      });
    }
  };

  submitGroupEvents = async (data: any, replaceExisting = false) => {
    const replacedCount = this.state.replaceGroupEventsCount;
    const groupName =
      this.state.groups.find(group => String(group.id) === String(data.tournamentGroupId))?.name || 'Group';
    this.setState({ isFixtureActionLoading: true, fixtureActionError: '' });
    try {
      const created = await addGroupEvents(this.props.user, this.props.tournament.id, {
        ...data,
        replaceExisting
      });
      const createdCount = Array.isArray(created) ? created.length : Number(created?.count || 0);
      this.setState({
        isCreateGroupEventsOpen: false,
        isReplaceGroupEventsOpen: false,
        pendingGroupEventsData: undefined,
        replaceGroupEventsCount: 0,
        isFixtureActionLoading: false,
        fixtureActionSuccess: '',
        groupEventsSuccess: { groupName, createdCount, replacedCount }
      });
      this.loadEvents(1);
    } catch (error) {
      this.setState({
        isFixtureActionLoading: false,
        fixtureActionError: propz.get(
          error,
          ['response', 'data', 'details', 'text'],
          'Unable to create group fixtures.'
        )
      });
    }
  };

  assignEventNumbers = async () => {
    const startFrom = Number(this.state.assignEventNumbersFrom);
    if (!Number.isInteger(startFrom) || startFrom < 0) {
      this.setState({ fixtureActionError: 'Enter a whole starting number.' });
      return;
    }
    this.setState({ isFixtureActionLoading: true, fixtureActionError: '' });
    try {
      const result = await assignTournamentEventNumbers(this.props.user, this.props.tournament.id, startFrom);
      this.setState({
        isAssignEventNumbersOpen: false,
        isFixtureActionLoading: false,
        fixtureActionSuccess: `Event numbers assigned to ${result.updated} fixtures.`
      });
      this.loadEvents(this.state.eventsPage);
    } catch (error) {
      this.setState({
        isFixtureActionLoading: false,
        fixtureActionError: propz.get(error, ['response', 'data', 'details', 'text'], 'Unable to assign event numbers.')
      });
    }
  };

  renderEvents = () => {
    const isTeam = this.isTeamCompetition();
    const isFixtureColumnVisible = (column: string) => !this.state.hiddenEventColumns.includes(column);
    const visibleFixtureColumnCount = FIXTURE_COLUMNS.filter(
      column => column.required || isFixtureColumnVisible(column.key)
    ).length;
    const title = isTeam ? 'Fixtures' : 'Events';
    const showParticipantLimit = !isTeam && this.shouldShowEventParticipantLimit([
      ...this.state.eventSportOptions,
      ...this.state.events
    ]);
    const allEventsOnPageSelected =
      this.state.events.length > 0 && this.state.events.every(event => this.state.selectedEventIds.includes(event.id));
    const selectedEvents = this.state.events.filter(event => this.state.selectedEventIds.includes(event.id));
    const activeEventFilters = [
      ...this.state.eventSportFilter.map(value => ({
        field: 'eventSportFilter' as const,
        value,
        label: `Sport: ${this.state.eventSportOptions.find(sport => sport.id === value)?.name || value}`
      })),
      ...this.state.eventAgeFilter.map(value => ({
        field: 'eventAgeFilter' as const,
        value,
        label: `Age: ${formatTournamentAges([Number(value)]) || value}`
      })),
      ...this.state.eventGenderFilter.map(value => ({
        field: 'eventGenderFilter' as const,
        value,
        label: `Gender: ${value === 'MALE_ONLY' ? 'Boys' : value === 'FEMALE_ONLY' ? 'Girls' : 'Mixed'}`
      })),
      ...(isTeam ? this.state.eventGroupFilter : []).map(value => ({
        field: 'eventGroupFilter' as const,
        value,
        label: `Group: ${(this.props.tournament.customGroups || []).find(group => group.id === value)?.name || value}`
      })),
      ...(isTeam ? this.state.eventPitchFilter : []).map(value => ({
        field: 'eventPitchFilter' as const,
        value,
        label:
          value === 'UNASSIGNED'
            ? 'Pitch: Not assigned'
            : `Pitch: ${(this.state.pitches || []).find(pitch => String(pitch.id) === String(value))?.name || value}`
      })),
      ...(isTeam ? this.state.eventStageFilter : []).map(value => {
        const stage = this.getEventStageOptions().find(item => item.id === value);
        return {
          field: 'eventStageFilter' as const,
          value,
          label: `Round: ${stage?.name || value}`
        };
      }),
      ...this.state.eventStatusFilter.map(value => ({
        field: 'eventStatusFilter' as const,
        value,
        label: `Status: ${EVENT_STATUS_OPTIONS.find(status => status.id === value)?.name || value}`
      }))
    ];
    const hasAnyEvents = (this.state.eventsUnfilteredTotal ?? this.state.eventsTotal) > 0;
    const formatEventTime = (value: string, includeDate: boolean) => {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return includeDate
        ? date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
          })
        : date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    };
    return (
      <article className="utmCompetition__panel utmCompetitionTeams utmCompetitionEvents">
        <header>
          <div>
            <h2>
              {title} <b>{this.state.eventsTotal}</b>
            </h2>
          </div>
          <div className="utmCompetitionEvents__viewActions">
            <button
              className={this.state.isEventFiltersOpen || activeEventFilters.length ? 'is-active' : ''}
              type="button"
              onClick={() =>
                this.setState(state => ({
                  isEventFiltersOpen: true,
                  eventSportFilterDraft: [...state.eventSportFilter],
                  eventAgeFilterDraft: [...state.eventAgeFilter],
                  eventGenderFilterDraft: [...state.eventGenderFilter],
                  eventGroupFilterDraft: isTeam ? [...state.eventGroupFilter] : [],
                  eventPitchFilterDraft: isTeam ? [...state.eventPitchFilter] : [],
                  eventStageFilterDraft: isTeam ? [...state.eventStageFilter] : [],
                  eventStatusFilterDraft: [...state.eventStatusFilter]
                }))
              }
            >
              <Icon path="M4 6h16M7 12h10M10 18h4" /> Filters
              {activeEventFilters.length ? <b>{activeEventFilters.length}</b> : null}
            </button>
            {isTeam ? (
              <button type="button" onClick={() => this.setState({ isEventColumnsOpen: true })}>
                <Icon path="M5 4h14v16H5zM9 4v16M15 4v16" /> Columns
              </button>
            ) : null}
          </div>
          <div className="utmCompetitionEvents__headerActions">
            <button className="utmCompetitionTeams__tool" type="button" onClick={this.props.onOpenLegacyEvents}>
              Bulk manage
            </button>
            {isTeam ? (
              <>
                <button
                  className="utmCompetitionTeams__tool"
                  type="button"
                  onClick={() =>
                    this.setState({ isAssignEventNumbersOpen: true, fixtureActionError: '', fixtureActionSuccess: '' })
                  }
                >
                  Assign event numbers
                </button>
                <button
                  className="utmCompetitionTeams__tool"
                  type="button"
                  onClick={async () => {
                    const pitches=await getTournamentPitches(this.props.user,this.props.tournament.id,{limit:3000,skip:0}).catch(()=>this.state.pitches);
                    this.setState({ isCreateGroupEventsOpen: true, fixtureActionError: '', fixtureActionSuccess: '', pitches:pitches||[] });
                  }}
                >
                  Create events for a group
                </button>
                <button className="utmCompetitionTeams__add" type="button" onClick={this.openAddFixtureModal}>
                  <span>+</span> Add fixture
                </button>
              </>
            ) : (
              <>
                <button
                  className="utmCompetitionTeams__tool"
                  type="button"
                  onClick={() => this.setState({ isAssignEventNumbersOpen: true, fixtureActionError: '', fixtureActionSuccess: '' })}
                >
                  Assign event numbers
                </button>
                <button className="utmCompetitionTeams__add" type="button" onClick={this.openAddFixtureModal}>
                  <span>+</span> Add event
                </button>
              </>
            )}
          </div>
        </header>
        {activeEventFilters.length ? (
          <div className="utmCompetitionEvents__activeFilters" aria-label="Applied fixture filters">
            {activeEventFilters.map(filter => (
              <span key={`${filter.field}-${filter.value}`}>
                {filter.label}
                <button
                  type="button"
                  aria-label={`Remove ${filter.label} filter`}
                  onClick={() => this.removeEventFilter(filter.field, filter.value)}
                >
                  ×
                </button>
              </span>
            ))}
            <button
              type="button"
              className="utmCompetitionEvents__clearFilters"
              onClick={() =>
                this.setState(
                  {
                    eventSportFilter: [],
                    eventAgeFilter: [],
                    eventGenderFilter: [],
                    eventGroupFilter: [],
                    eventPitchFilter: [],
                    eventStageFilter: [],
                    eventStatusFilter: [],
                    eventSportFilterDraft: [],
                    eventAgeFilterDraft: [],
                    eventGenderFilterDraft: [],
                    eventGroupFilterDraft: [],
                    eventPitchFilterDraft: [],
                    eventStageFilterDraft: [],
                    eventStatusFilterDraft: []
                  },
                  () => this.loadEvents(1)
                )
              }
            >
              Clear all
            </button>
          </div>
        ) : null}
        {this.state.fixtureActionSuccess ? (
          <div className="utmCompetitionEvents__success" role="status">
            {this.state.fixtureActionSuccess}
          </div>
        ) : null}
        {this.state.fixtureActionError &&
        !this.state.isAddFixtureOpen &&
        !this.state.isCreateGroupEventsOpen &&
        !this.state.isAssignEventNumbersOpen ? (
          <div className="utmCompetitionEvents__error" role="alert">
            {this.state.fixtureActionError}
          </div>
        ) : null}
        {this.state.selectedEventIds.length ? (
          <div className="utmCompetitionTeams__selection">
            <strong>Selected {this.state.selectedEventIds.length}</strong>
            {isTeam ? (
              <button type="button" onClick={() => this.setState({ isAssignEventPitchOpen: true })}>
                Assign pitch
              </button>
            ) : null}
            <button className="is-danger" type="button" onClick={() => this.setState({ isDeleteEventsOpen: true })}>
              Delete
            </button>
            <button className="is-clearSelection" type="button" onClick={() => this.setState({ selectedEventIds: [] })}>
              <span aria-hidden="true">×</span> Clear selection
            </button>
          </div>
        ) : null}
        <div className="utmCompetitionTeams__tableWrap">
          <table>
            <thead>
              {isTeam ? (
                <tr>
                  <th className="utmCompetitionTeams__selectColumn">
                    <input
                      type="checkbox"
                      aria-label="Select all fixtures on this page"
                      checked={allEventsOnPageSelected}
                      onChange={() =>
                        this.setState(state => ({
                          selectedEventIds: allEventsOnPageSelected
                            ? state.selectedEventIds.filter(id => !state.events.some(event => event.id === id))
                            : Array.from(new Set([...state.selectedEventIds, ...state.events.map(event => event.id)]))
                        }))
                      }
                    />
                  </th>
                  {isFixtureColumnVisible('number') ? <th>Event number</th> : null}
                  {isFixtureColumnVisible('time') ? <th>Time</th> : null}
                  {isFixtureColumnVisible('stage') ? <th>Competition / round</th> : null}
                  {isFixtureColumnVisible('pitch') ? <th>Pitch</th> : null}
                  <th>Fixture</th>
                  {isFixtureColumnVisible('score') ? <th>Score</th> : null}
                  {isFixtureColumnVisible('status') ? <th>Status</th> : null}
                </tr>
              ) : (
                <tr>
                  <th className="utmCompetitionTeams__selectColumn">
                    <input
                      type="checkbox"
                      aria-label="Select all events on this page"
                      checked={allEventsOnPageSelected}
                      onChange={() =>
                        this.setState(state => ({
                          selectedEventIds: allEventsOnPageSelected
                            ? state.selectedEventIds.filter(id => !state.events.some(event => event.id === id))
                            : Array.from(new Set([...state.selectedEventIds, ...state.events.map(event => event.id)]))
                        }))
                      }
                    />
                  </th>
                  <th>Date and time</th>
                  <th>Sport</th>
                  <th>Event</th>
                  <th>Age</th>
                  <th>Gender</th>
                  <th>Athletes</th>
                  {showParticipantLimit ? <th>Max per school</th> : null}
                  <th>Status</th>
                </tr>
              )}
            </thead>
            <tbody>
              {this.state.events.map(event => {
                const status = getEventStatus(event) || 'Not started';
                return isTeam ? (
                  <tr key={event.id} onClick={() => this.props.onOpenEvent(event, this.getEventListState())}>
                    <td onClick={clickEvent => clickEvent.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${this.getTournamentEventName(event)}`}
                        checked={this.state.selectedEventIds.includes(event.id)}
                        onChange={() =>
                          this.setState(state => ({
                            selectedEventIds: state.selectedEventIds.includes(event.id)
                              ? state.selectedEventIds.filter(id => id !== event.id)
                              : [...state.selectedEventIds, event.id]
                          }))
                        }
                      />
                    </td>
                    {isFixtureColumnVisible('number') ? (
                      <td>
                        <strong>{event.quantumEventNumber || <span className="is-empty">—</span>}</strong>
                      </td>
                    ) : null}
                    {isFixtureColumnVisible('time') ? (
                      <td>
                        <strong>{formatEventTime(event.startTime, false)}</strong>
                      </td>
                    ) : null}
                    {isFixtureColumnVisible('stage') ? (
                      <td>
                        {(() => {
                          const stage = this.getTournamentEventStage(event);
                          return stage.competition || stage.round ? (
                            <span className="utmCompetitionEvents__stage">
                              <strong>{stage.competition || 'Knockout'}</strong>
                              <small>{stage.round}</small>
                            </span>
                          ) : <span className="is-empty">—</span>;
                        })()}
                      </td>
                    ) : null}
                    {isFixtureColumnVisible('pitch') ? (
                      <td>{this.getTournamentEventPitch(event) || <span className="is-empty">—</span>}</td>
                    ) : null}
                    <td className="utmCompetitionEvents__name">
                      <strong>{this.getTournamentEventName(event)}</strong>
                    </td>
                    {isFixtureColumnVisible('score') ? (
                      <td>{this.getTournamentEventScore(event) || <span className="is-empty">—</span>}</td>
                    ) : null}
                    {isFixtureColumnVisible('status') ? (
                      <td>
                        <span
                          className={`utmCompetitionEvents__status is-${String(
                            event.status || 'scheduled'
                          ).toLowerCase()}`}
                        >
                          {status}
                        </span>
                      </td>
                    ) : null}
                  </tr>
                ) : (
                  <tr key={event.id} onClick={() => this.props.onOpenEvent(event, this.getEventListState())}>
                    <td onClick={clickEvent => clickEvent.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${this.getTournamentEventName(event)}`}
                        checked={this.state.selectedEventIds.includes(event.id)}
                        onChange={() =>
                          this.setState(state => ({
                            selectedEventIds: state.selectedEventIds.includes(event.id)
                              ? state.selectedEventIds.filter(id => id !== event.id)
                              : [...state.selectedEventIds, event.id]
                          }))
                        }
                      />
                    </td>
                    <td>
                      <strong>{formatEventTime(event.startTime, true)}</strong>
                    </td>
                    <td>{event.sport?.name || <span className="is-empty">—</span>}</td>
                    <td className="utmCompetitionEvents__name">
                      <strong>{this.getTournamentEventName(event)}</strong>
                    </td>
                    <td>{formatTournamentAges(event.ages) || <span className="is-empty">—</span>}</td>
                    <td>{getEventGender(event) || <span className="is-empty">—</span>}</td>
                    <td>
                      <strong>
                        {getUniqueEventAthleteCount(event)}
                      </strong>
                    </td>
                    {showParticipantLimit ? (
                      <td>
                        {this.getEventParticipantLimit(event) > 0 ? (
                          <span className="utmCompetitionEvents__participantLimit">
                            {this.getEventParticipantLimit(event)} max
                          </span>
                        ) : <span className="is-empty">—</span>}
                      </td>
                    ) : null}
                    <td>
                      <span
                        className={`utmCompetitionEvents__status is-${String(
                          event.status || 'scheduled'
                        ).toLowerCase()}`}
                      >
                        {status}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {!this.state.isEventsLoading && !this.state.events.length ? (
                <tr>
                  <td colSpan={isTeam ? visibleFixtureColumnCount + 1 : 8 + (showParticipantLimit ? 1 : 0)} className="utmCompetitionTeams__empty">
                    <div className="utmCompetitionTeams__emptyState">
                      <span aria-hidden="true">
                        <Icon
                          path={
                            activeEventFilters.length && hasAnyEvents
                              ? 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM16 16l4 4'
                              : 'M7 3v3M17 3v3M4 8h16M5 5h14a1 1 0 0 1 1 1v14H4V6a1 1 0 0 1 1-1z'
                          }
                        />
                      </span>
                      <strong>
                        {activeEventFilters.length && hasAnyEvents
                          ? `No ${isTeam ? 'fixtures' : 'events'} match your filters`
                          : `No ${isTeam ? 'fixtures' : 'events'} yet`}
                      </strong>
                      <p>
                        {activeEventFilters.length && hasAnyEvents
                          ? 'Try adjusting or clearing one or more filters.'
                          : `Add your first ${
                              isTeam ? 'fixture' : 'event'
                            } to start building the competition schedule.`}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <Pagination2
          modern
          currentPage={this.state.eventsPage}
          total={this.state.eventsTotal}
          limit={20}
          setCurrentPageParams={this.loadEvents}
        />
        {this.state.isEventFiltersOpen ? (
          <div className="utmEligiblePanelBackdrop" onMouseDown={() => this.setState({ isEventFiltersOpen: false })}>
            <form
              className="utmEligiblePanel utmFixtureFiltersPanel"
              onMouseDown={event => event.stopPropagation()}
              onSubmit={event => {
                event.preventDefault();
                this.setState(
                  state => ({
                    eventSportFilter: [...state.eventSportFilterDraft],
                    eventAgeFilter: [...state.eventAgeFilterDraft],
                    eventGenderFilter: [...state.eventGenderFilterDraft],
                    eventGroupFilter: isTeam ? [...state.eventGroupFilterDraft] : [],
                    eventPitchFilter: isTeam ? [...state.eventPitchFilterDraft] : [],
                    eventStageFilter: isTeam ? [...state.eventStageFilterDraft] : [],
                    eventStatusFilter: [...state.eventStatusFilterDraft],
                    isEventFiltersOpen: false
                  }),
                  () => this.loadEvents(1)
                );
              }}
            >
              <header>
                <div>
                  <h2>{isTeam ? 'Fixture' : 'Event'} filters</h2>
                  <p>
                    {isTeam
                      ? 'Narrow the list by sport, age, gender, status, group, pitch or stage.'
                      : 'Narrow the list by sport, age, gender or status.'}
                  </p>
                </div>
                <button type="button" onClick={() => this.setState({ isEventFiltersOpen: false })}>
                  ×
                </button>
              </header>
              <div className="utmEligiblePanel__body">
                <section>
                  <SearchMultiSelectFilter
                    label="Sports"
                    options={this.state.eventSportOptions.map(sport => ({ value: sport.id, label: sport.name }))}
                    selected={this.state.eventSportFilterDraft}
                    onToggle={value => this.toggleEventFilterDraft('eventSportFilterDraft', value)}
                    placeholder="Search and select sports…"
                    emptyText="No matching sports"
                  />
                </section>
                <section>
                  <h3>Ages</h3>
                  <div className="utmEligiblePanel__chips">
                    {(this.props.tournament.ages || []).map(age => {
                      const value = String(age);
                      return (
                        <button
                          type="button"
                          key={value}
                          className={this.state.eventAgeFilterDraft.includes(value) ? 'is-selected' : ''}
                          onClick={() => this.toggleEventFilterDraft('eventAgeFilterDraft', value)}
                        >
                          {formatTournamentAges([age]) || value}
                        </button>
                      );
                    })}
                  </div>
                </section>
                <section>
                  <h3>Gender</h3>
                  <div className="utmEligiblePanel__chips">
                    {[
                      { id: 'MALE_ONLY', name: 'Boys' },
                      { id: 'FEMALE_ONLY', name: 'Girls' },
                      { id: 'MIXED', name: 'Mixed' }
                    ].map(gender => (
                      <button
                        type="button"
                        key={gender.id}
                        className={this.state.eventGenderFilterDraft.includes(gender.id) ? 'is-selected' : ''}
                        onClick={() => this.toggleEventFilterDraft('eventGenderFilterDraft', gender.id)}
                      >
                        {gender.name}
                      </button>
                    ))}
                  </div>
                </section>
                {isTeam ? (
                  <section>
                    <h3>Groups &amp; competitions</h3>
                    <div className="utmEligiblePanel__chips">
                      {(this.props.tournament.customGroups || []).map(group => (
                        <button
                          type="button"
                          key={group.id}
                          className={this.state.eventGroupFilterDraft.includes(group.id) ? 'is-selected' : ''}
                          onClick={() => this.toggleEventFilterDraft('eventGroupFilterDraft', group.id)}
                        >
                          {group.name}
                        </button>
                      ))}
                    </div>
                  </section>
                ) : null}
                {isTeam ? (
                  <section>
                    <h3>Pitches</h3>
                    <div className="utmEligiblePanel__chips">
                      <button
                        type="button"
                        className={this.state.eventPitchFilterDraft.includes('UNASSIGNED') ? 'is-selected' : ''}
                        onClick={() => this.toggleEventFilterDraft('eventPitchFilterDraft', 'UNASSIGNED')}
                      >
                        Not assigned
                      </button>
                      {(this.state.pitches || []).map(pitch => (
                        <button
                          type="button"
                          key={pitch.id}
                          className={this.state.eventPitchFilterDraft.includes(pitch.id) ? 'is-selected' : ''}
                          onClick={() => this.toggleEventFilterDraft('eventPitchFilterDraft', pitch.id)}
                        >
                          {pitch.name}
                        </button>
                      ))}
                    </div>
                  </section>
                ) : null}
                {isTeam ? (
                  <section>
                    <h3>Rounds</h3>
                    <div className="utmEligiblePanel__chips">
                      {this.getEventStageOptions().map(stage => (
                        <button
                          type="button"
                          key={stage.id}
                          className={this.state.eventStageFilterDraft.includes(stage.id) ? 'is-selected' : ''}
                          onClick={() => this.toggleEventFilterDraft('eventStageFilterDraft', stage.id)}
                        >
                          {stage.name || 'Round'}
                        </button>
                      ))}
                    </div>
                  </section>
                ) : null}
                <section>
                  <h3>Statuses</h3>
                  <div className="utmEligiblePanel__chips">
                    {EVENT_STATUS_OPTIONS.map(status => (
                      <button
                        type="button"
                        key={status.id}
                        className={this.state.eventStatusFilterDraft.includes(status.id) ? 'is-selected' : ''}
                        onClick={() => this.toggleEventFilterDraft('eventStatusFilterDraft', status.id)}
                      >
                        {status.name}
                      </button>
                    ))}
                  </div>
                </section>
              </div>
              <footer>
                <button
                  type="button"
                  onClick={() =>
                    this.setState({
                      eventSportFilterDraft: [],
                      eventAgeFilterDraft: [],
                      eventGenderFilterDraft: [],
                      eventGroupFilterDraft: [],
                      eventPitchFilterDraft: [],
                      eventStageFilterDraft: [],
                      eventStatusFilterDraft: []
                    })
                  }
                >
                  Clear all
                </button>
                <button type="button" onClick={() => this.setState({ isEventFiltersOpen: false })}>
                  Cancel
                </button>
                <button className="is-primary" type="submit">
                  Apply
                </button>
              </footer>
            </form>
          </div>
        ) : null}
        {this.state.isEventColumnsOpen ? (
          <div className="utmColumnsBackdrop" onMouseDown={() => this.setState({ isEventColumnsOpen: false })}>
            <div className="utmColumnsModal utmFixtureColumnsModal" onMouseDown={event => event.stopPropagation()}>
              <header>
                <div>
                  <h2>Fixture columns</h2>
                  <p>Choose which details appear in the table.</p>
                </div>
                <button type="button" className="utmColumnsReset" onClick={this.resetFixtureColumns}>
                  Reset
                </button>
                <button type="button" onClick={() => this.setState({ isEventColumnsOpen: false })}>
                  ×
                </button>
              </header>
              <div>
                {FIXTURE_COLUMNS.map(column => (
                  <label key={column.key} className={column.required ? 'is-required' : ''}>
                    <input
                      type="checkbox"
                      checked={column.required || isFixtureColumnVisible(column.key)}
                      disabled={column.required}
                      onChange={() => this.toggleFixtureColumn(column.key)}
                    />
                    <span>{column.label}</span>
                    {column.required ? <small>Always shown</small> : null}
                  </label>
                ))}
              </div>
              <footer>
                <button type="button" onClick={() => this.setState({ isEventColumnsOpen: false })}>
                  Done
                </button>
              </footer>
            </div>
          </div>
        ) : null}
        <SimpleModal
          isOpen={this.state.isAddFixtureOpen}
          title={isTeam ? 'Add fixture' : 'Add tournament event'}
          customClass="utmFixtureModal"
          dialogStyle={{ maxWidth: 760 }}
          showFooter={false}
          isBodyBold={false}
          onCloseClick={this.requestCloseFixture}
          onBackdropClick={this.requestCloseFixture}
        >
          {this.state.fixtureActionError ? (
            <div className="utmFixtureModal__error" role="alert">
              {this.state.fixtureActionError}
            </div>
          ) : null}
          <TournamentAddEventForm
            tournament={this.props.tournament}
            onSubmit={this.createFixture}
            onCancel={this.requestCloseFixture}
            onDirtyChange={this.handleFixtureDirtyChange}
            user={this.props.user}
            participants={this.state.fixtureParticipants}
            isSubmitting={this.state.isFixtureActionLoading}
          />
        </SimpleModal>
        <UnsavedChangesModal
          isOpen={this.state.isDiscardFixtureChangesOpen}
          message={`You have unsaved ${
            isTeam ? 'fixture' : 'event'
          } details. If you close now, those changes will be lost.`}
          onKeepEditing={() => this.setState({ isDiscardFixtureChangesOpen: false })}
          onDiscard={this.discardFixtureChanges}
        />
        <SimpleModal
          isOpen={this.state.isCreateGroupEventsOpen}
          title="Create events for a group"
          customClass="utmGroupEventsModal"
          dialogStyle={{ maxWidth: 620 }}
          showFooter={false}
          isBodyBold={false}
          onCloseClick={() => !this.state.isFixtureActionLoading && this.setState({ isCreateGroupEventsOpen: false, fixtureActionError: '' })}
        >
          {this.state.fixtureActionError ? (
            <div className="utmFixtureModal__error" role="alert">
              {this.state.fixtureActionError}
            </div>
          ) : null}
          <TournamentEventsGroupForm
            user={this.props.user}
            tournament={{...this.props.tournament,customGroups:this.state.groups,pitches:this.state.pitches}}
            onSubmit={this.createEventsForGroup}
            onCancel={() => !this.state.isFixtureActionLoading && this.setState({ isCreateGroupEventsOpen: false, fixtureActionError: '' })}
            isSubmitting={this.state.isFixtureActionLoading}
          />
        </SimpleModal>
        <SimpleModal
          isOpen={this.state.isReplaceGroupEventsOpen}
          isBare
          onBackdropClick={() =>
            !this.state.isFixtureActionLoading &&
            this.setState({
              isReplaceGroupEventsOpen: false,
              pendingGroupEventsData: undefined,
              replaceGroupEventsCount: 0,
              fixtureActionError: ''
            })
          }
        >
          <section className="utmGroupEventsReplace" role="alertdialog" aria-modal="true">
            <header>
              <span aria-hidden="true">!</span>
              <div>
                <h2>Replace existing group events?</h2>
                <p>
                  This group already has {this.state.replaceGroupEventsCount}{' '}
                  {this.state.replaceGroupEventsCount === 1 ? 'event' : 'events'}.
                </p>
              </div>
              <button
                type="button"
                aria-label="Close"
                disabled={this.state.isFixtureActionLoading}
                onClick={() =>
                  this.setState({
                    isReplaceGroupEventsOpen: false,
                    pendingGroupEventsData: undefined,
                    replaceGroupEventsCount: 0,
                    fixtureActionError: ''
                  })
                }
              >
                ×
              </button>
            </header>
            <div>
              <strong>
                All existing events in{' '}
                {this.state.groups.find(
                  group => String(group.id) === String(this.state.pendingGroupEventsData?.tournamentGroupId)
                )?.name || 'this group'}{' '}
                will be deleted and replaced.
              </strong>
              <p>Results, pitch assignments and any changes made to those events will also be removed.</p>
              {this.state.fixtureActionError ? (
                <div className="utmFixtureModal__error" role="alert">
                  {this.state.fixtureActionError}
                </div>
              ) : null}
            </div>
            <footer>
              <button
                className="is-secondary"
                type="button"
                disabled={this.state.isFixtureActionLoading}
                onClick={() =>
                  this.setState({
                    isReplaceGroupEventsOpen: false,
                    pendingGroupEventsData: undefined,
                    replaceGroupEventsCount: 0,
                    fixtureActionError: ''
                  })
                }
              >
                Keep existing events
              </button>
              <button
                className="is-danger"
                type="button"
                disabled={this.state.isFixtureActionLoading || !this.state.pendingGroupEventsData}
                onClick={() =>
                  this.state.pendingGroupEventsData && this.submitGroupEvents(this.state.pendingGroupEventsData, true)
                }
              >
                {this.state.isFixtureActionLoading ? 'Replacing…' : 'Replace events'}
              </button>
            </footer>
          </section>
        </SimpleModal>
        <SimpleModal
          isOpen={Boolean(this.state.groupEventsSuccess)}
          isBare
          onBackdropClick={() => this.setState({ groupEventsSuccess: undefined })}
        >
          <section className="utmGroupEventsSuccess" role="status" aria-live="polite">
            <div className="utmGroupEventsSuccess__mark" aria-hidden="true">
              ✓
            </div>
            <h2>Group events created</h2>
            <p>
              {this.state.groupEventsSuccess?.createdCount || 'New'}{' '}
              {this.state.groupEventsSuccess?.createdCount === 1 ? 'event has' : 'events have'} been created for{' '}
              <strong>{this.state.groupEventsSuccess?.groupName}</strong>.
            </p>
            {this.state.groupEventsSuccess?.replacedCount ? (
              <small>
                {this.state.groupEventsSuccess.replacedCount}{' '}
                {this.state.groupEventsSuccess.replacedCount === 1 ? 'previous event was' : 'previous events were'}{' '}
                replaced.
              </small>
            ) : null}
            <button type="button" onClick={() => this.setState({ groupEventsSuccess: undefined })}>
              View events
            </button>
          </section>
        </SimpleModal>
        <SimpleModal
          isOpen={this.state.isAssignEventNumbersOpen}
          title="Assign event numbers"
          customClass="utmFixtureNumbersModal"
          dialogStyle={{ maxWidth: 430 }}
          showFooter={false}
          isBodyBold={false}
          onCloseClick={() => this.setState({ isAssignEventNumbersOpen: false, fixtureActionError: '' })}
        >
          <form
            className="utmFixtureNumbersForm"
            onSubmit={event => {
              event.preventDefault();
              if (!this.state.isFixtureActionLoading) this.assignEventNumbers();
            }}
          >
            <p>Fixtures will be numbered sequentially in start-time order.</p>
            <label>
              <span>Starting number</span>
              <input
                type="number"
                min="0"
                step="1"
                value={this.state.assignEventNumbersFrom}
                onChange={event =>
                  this.setState({ assignEventNumbersFrom: event.target.value, fixtureActionError: '' })
                }
                autoFocus
              />
            </label>
            {this.state.fixtureActionError ? (
              <div className="utmFixtureModal__error" role="alert">
                {this.state.fixtureActionError}
              </div>
            ) : null}
            <footer>
              <button
                className="is-secondary"
                type="button"
                onClick={() => this.setState({ isAssignEventNumbersOpen: false, fixtureActionError: '' })}
              >
                Cancel
              </button>
              <button className="is-primary" type="submit" disabled={this.state.isFixtureActionLoading}>
                {this.state.isFixtureActionLoading ? 'Assigning…' : 'Assign numbers'}
              </button>
            </footer>
          </form>
        </SimpleModal>
        <SimpleModal
          isOpen={this.state.isAssignEventPitchOpen}
          title="Assign pitch"
          customClass="utmEventPitchModal"
          dialogStyle={{ maxWidth: 420 }}
          showFooter={false}
          isBodyBold={false}
          onCloseClick={() => this.setState({ isAssignEventPitchOpen: false, selectedEventPitchId: '' })}
        >
          <div className="utmEventPitchForm">
            <p>{this.state.selectedEventIds.length} selected fixtures</p>
            <label>
              <span>Pitch</span>
              <select
                value={this.state.selectedEventPitchId}
                onChange={event => this.setState({ selectedEventPitchId: event.target.value })}
              >
                <option value="">Not assigned</option>
                {(this.props.tournament.pitches || []).map(pitch => (
                  <option key={pitch.id} value={pitch.id}>
                    {pitch.name}
                  </option>
                ))}
              </select>
            </label>
            <footer>
              <button
                className="is-secondary"
                type="button"
                onClick={() => this.setState({ isAssignEventPitchOpen: false })}
              >
                Cancel
              </button>
              <button
                className="is-primary"
                type="button"
                disabled={this.state.isAssigningEventPitch}
                onClick={this.assignPitchToSelectedEvents}
              >
                {this.state.isAssigningEventPitch ? 'Saving…' : 'Save changes'}
              </button>
            </footer>
          </div>
        </SimpleModal>
        <SimpleModal
          isOpen={this.state.isDeleteEventsOpen}
          title={`Delete selected ${isTeam ? 'fixtures' : 'events'}?`}
          customClass="unionTournamentDeleteModal utmEventDeleteModal"
          dialogStyle={{ maxWidth: 440 }}
          buttonText={this.state.isDeletingEvents ? 'Deleting…' : 'Delete'}
          onButtonClick={this.deleteSelectedEvents}
          onCloseClick={() => this.setState({ isDeleteEventsOpen: false })}
        >
          <p>{selectedEvents.map(event => this.getTournamentEventName(event)).join(', ')}</p>
          <p>This action cannot be undone.</p>
        </SimpleModal>
      </article>
    );
  };

  renderPitches = () => {
    const { pitches, selectedPitchIds } = this.state;
    const allSelected = pitches.length > 0 && pitches.every(pitch => selectedPitchIds.includes(pitch.id));
    const selectedPitches = pitches.filter(pitch => selectedPitchIds.includes(pitch.id));
    const renderFields = () => (
      <div className="utmPitchForm">
        <label>
          <span>Pitch name</span>
          <input
            type="text"
            value={this.state.pitchName}
            onChange={event => this.setState({ pitchName: event.target.value, pitchFormError: '' })}
            placeholder="For example, Pitch 1"
            autoFocus
          />
        </label>
        <div className="utmPitchForm__row">
          <label>
            <span>Surface</span>
            <input
              type="text"
              value={this.state.pitchSurface}
              onChange={event => this.setState({ pitchSurface: event.target.value })}
              placeholder="Grass, 3G…"
            />
          </label>
          <label>
            <span>Size</span>
            <input
              type="text"
              value={this.state.pitchSize}
              onChange={event => this.setState({ pitchSize: event.target.value })}
              placeholder="Full, 7-a-side…"
            />
          </label>
        </div>
        <label>
          <span>Note</span>
          <textarea
            value={this.state.pitchNote}
            onChange={event => this.setState({ pitchNote: event.target.value })}
            placeholder="Optional location or access note"
          />
        </label>
        {this.state.pitchFormError ? <div className="utmGroupForm__error">{this.state.pitchFormError}</div> : null}
      </div>
    );
    return (
      <>
        <article className="utmCompetition__panel utmCompetitionTeams utmCompetitionPitches">
          <header>
            <div>
              <div className="utmCompetitionPitches__titleRow">
                <h2>
                  Pitches <b>{this.state.pitchesTotal}</b>
                </h2>
                <form
                  className="utmCompetitionPitches__search"
                  onSubmit={event => {
                    event.preventDefault();
                    this.loadPitches(1, this.state.pitchesSearchDraft.trim());
                  }}
                >
                  <label className="sr-only" htmlFor="tournament-pitches-search">
                    Search pitches by name
                  </label>
                  <input
                    id="tournament-pitches-search"
                    type="search"
                    value={this.state.pitchesSearchDraft}
                    onChange={event => this.setState({ pitchesSearchDraft: event.target.value })}
                    placeholder="Search pitches by name"
                  />
                </form>
              </div>
              <p>Playing areas at the venue. Fixtures and groups are scheduled onto them.</p>
            </div>
            <button className="utmCompetitionTeams__add" type="button" onClick={this.openAddPitch}>
              <span>+</span> Add pitch
            </button>
          </header>
          {this.state.pitchesSearch ? (
            <div className="utmCompetitionTeams__filters">
              <span>
                Name: {this.state.pitchesSearch}
                <button type="button" aria-label="Clear pitch name search" onClick={() => this.loadPitches(1, '')}>
                  ×
                </button>
              </span>
            </div>
          ) : null}
          {selectedPitchIds.length ? (
            <div className="utmCompetitionTeams__selection">
              <strong>Selected {selectedPitchIds.length}</strong>
              <button className="is-danger" type="button" onClick={() => this.setState({ isDeletePitchesOpen: true })}>
                Delete
              </button>
              <button
                className="is-clearSelection"
                type="button"
                onClick={() => this.setState({ selectedPitchIds: [] })}
              >
                <span aria-hidden="true">×</span> Clear selection
              </button>
            </div>
          ) : null}
          <div className="utmCompetitionTeams__tableWrap">
            <table>
              <thead>
                <tr>
                  <th className="utmCompetitionTeams__selectColumn">
                    <input
                      type="checkbox"
                      aria-label="Select all pitches on this page"
                      checked={allSelected}
                      onChange={() =>
                        this.setState(state => ({
                          selectedPitchIds: allSelected
                            ? state.selectedPitchIds.filter(id => !pitches.some(pitch => pitch.id === id))
                            : Array.from(new Set([...state.selectedPitchIds, ...pitches.map(pitch => pitch.id)]))
                        }))
                      }
                    />
                  </th>
                  <th>Pitch</th>
                  <th>Surface</th>
                  <th>Size</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {pitches.map(pitch => (
                  <tr key={pitch.id} onClick={() => this.openPitchDrawer(pitch)}>
                    <td onClick={event => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${pitch.name}`}
                        checked={selectedPitchIds.includes(pitch.id)}
                        onChange={() =>
                          this.setState(state => ({
                            selectedPitchIds: state.selectedPitchIds.includes(pitch.id)
                              ? state.selectedPitchIds.filter(id => id !== pitch.id)
                              : [...state.selectedPitchIds, pitch.id]
                          }))
                        }
                      />
                    </td>
                    <td>
                      <strong>{pitch.name}</strong>
                    </td>
                    <td>{pitch.surface || <span className="is-empty">—</span>}</td>
                    <td>{pitch.size || <span className="is-empty">—</span>}</td>
                    <td className="utmCompetitionPitches__note" title={pitch.note || undefined}>
                      {pitch.note || <span className="is-empty">—</span>}
                    </td>
                  </tr>
                ))}
                {!this.state.isPitchesLoading && !pitches.length ? (
                  <tr>
                    <td colSpan={5} className="utmCompetitionTeams__empty">
                      <div className="utmTableEmptyState">
                        <span aria-hidden="true">
                          <Icon
                            path={
                              this.state.pitchesSearch && (this.state.pitchesUnfilteredTotal ?? 0) > 0
                                ? 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM16 16l4 4'
                                : 'M3 5h18v14H3zM12 5v14M3 12h18M15 12a3 3 0 1 1-6 0 3 3 0 1 1 6 0'
                            }
                          />
                        </span>
                        <strong>
                          {this.state.pitchesSearch && (this.state.pitchesUnfilteredTotal ?? 0) > 0
                            ? 'No pitches match your search'
                            : 'No pitches yet'}
                        </strong>
                        <p>
                          {this.state.pitchesSearch && (this.state.pitchesUnfilteredTotal ?? 0) > 0
                            ? 'Try a different pitch name or clear the search.'
                            : 'Add your first pitch to start planning where fixtures will be played.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <Pagination2
            modern
            currentPage={this.state.pitchesPage}
            total={this.state.pitchesTotal}
            limit={20}
            setCurrentPageParams={this.loadPitches}
          />
        </article>
        <SimpleModal
          isOpen={this.state.isAddPitchOpen}
          title="Add pitch"
          customClass="utmPitchFormModal"
          dialogStyle={{ maxWidth: 480 }}
          showFooter={false}
          isBodyBold={false}
          onCloseClick={() => this.setState({ isAddPitchOpen: false, pitchFormError: '' })}
        >
          <form
            onSubmit={event => {
              event.preventDefault();
              if (!this.state.isSavingPitch) this.savePitch(true);
            }}
          >
            {renderFields()}
            <footer className="utmPitchForm__footer">
              <button className="is-secondary" type="button" onClick={() => this.setState({ isAddPitchOpen: false })}>
                Cancel
              </button>
              <button className="is-primary" type="submit" disabled={this.state.isSavingPitch}>
                {this.state.isSavingPitch ? 'Adding…' : 'Add pitch'}
              </button>
            </footer>
          </form>
        </SimpleModal>
        {this.state.openedPitch && !this.state.isDiscardPitchChangesOpen ? (
          <div
            className="utmConfirmationDrawer utmPitchDrawer"
            onMouseDown={event => event.target === event.currentTarget && this.requestClosePitchDrawer()}
          >
            <aside>
              <header>
                <div>
                  <h3>{this.state.openedPitch.name}</h3>
                  <p>Pitch details</p>
                </div>
                <button type="button" onClick={this.requestClosePitchDrawer} aria-label="Close pitch details">
                  ×
                </button>
              </header>
              <div className="utmConfirmationDrawer__body">{renderFields()}</div>
              <footer>
                <button className="is-secondary" type="button" onClick={this.requestClosePitchDrawer}>
                  Cancel
                </button>
                <button
                  className="is-primary"
                  type="button"
                  disabled={this.state.isSavingPitch || !this.hasUnsavedPitchChanges()}
                  onClick={() => this.savePitch(false)}
                >
                  {this.state.isSavingPitch ? 'Saving…' : 'Save changes'}
                </button>
              </footer>
            </aside>
          </div>
        ) : null}
        <UnsavedChangesModal
          isOpen={this.state.isDiscardPitchChangesOpen}
          message="Your changes to this pitch have not been saved. If you leave now, they will be lost."
          onKeepEditing={() => this.setState({ isDiscardPitchChangesOpen: false })}
          onDiscard={() =>
            this.setState({ openedPitch: undefined, isDiscardPitchChangesOpen: false, pitchFormError: '' })
          }
        />
        <SimpleModal
          isOpen={this.state.isDeletePitchesOpen}
          title="Delete selected pitches?"
          customClass="unionTournamentDeleteModal utmPitchDeleteModal"
          dialogStyle={{ maxWidth: 420 }}
          buttonText={this.state.isDeletingPitches ? 'Deleting…' : 'Delete'}
          onButtonClick={this.deleteSelectedPitches}
          onCloseClick={() => this.setState({ isDeletePitchesOpen: false })}
        >
          <p>{selectedPitches.map(pitch => pitch.name).join(', ')}</p>
          <p>This action cannot be undone.</p>
        </SimpleModal>
      </>
    );
  };

  renderGroups = () => {
    const { groups, groupTeams, selectedGroupIds } = this.state;
    const formatGroups = [...groups]
      .sort((left, right) => String(left.name || '').localeCompare(String(right.name || ''), undefined, {
        numeric: true,
        sensitivity: 'base'
      }));
    const normalizedSearch = this.state.groupsAppliedSearch.trim().toLowerCase();
    const visibleGroups = normalizedSearch
      ? formatGroups.filter(group =>
          String(group.name || '')
            .toLowerCase()
            .includes(normalizedSearch)
        )
      : formatGroups;
    const selectedGroups = formatGroups.filter(group => selectedGroupIds.includes(group.id));
    const isAllSelected = visibleGroups.length > 0 && visibleGroups.every(group => selectedGroupIds.includes(group.id));
    const teamCountFor = (groupId: string) =>
      groupTeams.filter(team => (team.groupIds || []).map(String).includes(String(groupId))).length;
    return (
      <>
        <article className="utmCompetition__panel utmCompetitionTeams utmCompetitionGroups">
          <header>
            <div className="utmCompetitionGroups__heading">
              <div className="utmCompetitionGroups__titleRow">
                <h2>
                  Format &amp; Structure <b>{formatGroups.length}</b>
                </h2>
                <label className="utmCompetitionGroups__search">
                  <span className="sr-only">Search groups or competitions</span>
                  <input
                    type="search"
                    value={this.state.groupsSearch}
                    onChange={event => this.setState({ groupsSearch: event.target.value })}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        this.setState({ groupsAppliedSearch: this.state.groupsSearch });
                      }
                    }}
                    placeholder="Search groups or competitions"
                  />
                </label>
              </div>
            </div>
            <div className="utmCompetitionGroups__actions">
              <button className="utmCompetitionTeams__tool" type="button" onClick={this.props.onOpenGroupsGrid}>
                <Icon path="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" /> Group grid view
              </button>
              <TournamentCompetitionStructure user={this.props.user} tournament={this.props.tournament} />
              <button
                className="utmCompetitionTeams__tool"
                type="button"
                disabled={this.state.isSavingDefaultGroups}
                onClick={() => this.setState({ isDefaultGroupsOpen: true })}
              >
                <Icon path="M12 5v14M5 12h14" /> Add default groups
              </button>
              <button className="utmCompetitionGroups__manage" type="button" onClick={this.openAddCustomGroup}>
                <Icon path="M12 5v14M5 12h14" />
                Add custom group
              </button>
            </div>
          </header>
          {selectedGroupIds.length ? (
            <div className="utmCompetitionTeams__selection">
              <strong>Selected {selectedGroupIds.length}</strong>
              <button type="button" onClick={this.props.onManageGroups}>
                Set pitch
              </button>
              <button className="is-danger" type="button" onClick={() => this.setState({ isDeleteGroupsOpen: true })}>
                Delete
              </button>
              <button
                className="is-clearSelection"
                type="button"
                onClick={() => this.setState({ selectedGroupIds: [] })}
              >
                <span aria-hidden="true">×</span> Clear selection
              </button>
            </div>
          ) : null}
          <div className="utmCompetitionTeams__tableWrap">
            <table>
              <thead>
                <tr>
                  <th className="utmCompetitionTeams__selectColumn">
                    <input
                      type="checkbox"
                      aria-label="Select all groups"
                      checked={isAllSelected}
                      onChange={() =>
                        this.setState(state => {
                          const visibleIds = visibleGroups.map(group => group.id);
                          return {
                            selectedGroupIds: isAllSelected
                              ? state.selectedGroupIds.filter(id => !visibleIds.includes(id))
                              : Array.from(new Set([...state.selectedGroupIds, ...visibleIds]))
                          };
                        })
                      }
                    />
                  </th>
                  <th>Competition</th>
                  <th>Stage</th>
                  <th className="is-centred">Teams</th>
                  <th>Pitch</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {visibleGroups.map(group => {
                  const stage = this.getGroupStage(group);
                  const teamsCount = teamCountFor(group.id);
                  const pitch = this.getGroupPitch(group);
                  return (
                    <tr key={group.id} onClick={() => this.openGroupDrawer(group)}>
                      <td onClick={event => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${group.name}`}
                          checked={selectedGroupIds.includes(group.id)}
                          onChange={() =>
                            this.setState(state => ({
                              selectedGroupIds: state.selectedGroupIds.includes(group.id)
                                ? state.selectedGroupIds.filter(id => id !== group.id)
                                : [...state.selectedGroupIds, group.id]
                            }))
                          }
                        />
                      </td>
                      <td>
                        <strong>{group.name || 'Unnamed group'}</strong>
                      </td>
                      <td>
                        <span className={`utmCompetitionGroups__stage is-${stage.type.toLowerCase()}`}>
                          {stage.label}
                        </span>
                      </td>
                      <td className="is-centred">{teamsCount || <span className="is-empty">—</span>}</td>
                      <td className={pitch === 'Not set' ? 'is-empty' : undefined}>{pitch}</td>
                      <td className="utmCompetitionGroups__rowActions" onClick={event => event.stopPropagation()}>
                        <button type="button" className="utmCompetitionTeams__tool" aria-label={`Edit ${group.name || 'unnamed group'}`} onClick={() => this.openGroupDrawer(group)}>Edit</button>
                      </td>
                    </tr>
                  );
                })}
                {!visibleGroups.length ? (
                  <tr>
                    <td colSpan={6} className="utmCompetitionTeams__empty">
                      <div className="utmTableEmptyState">
                        <span aria-hidden="true">
                          <Icon
                            path={
                              groups.length
                                ? 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM16 16l4 4'
                                : 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z'
                            }
                          />
                        </span>
                        <strong>{groups.length ? 'No groups match your search' : 'No groups yet'}</strong>
                        <p>
                          {groups.length
                            ? 'Try a different group name or clear the search.'
                            : 'Add default groups or create your first custom group to organise the competition.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <div className="utmCompetitionGroups__footer">
            Showing {visibleGroups.length} of {groups.length}
          </div>
        </article>
        <SimpleModal
          isOpen={this.state.isDefaultGroupsOpen}
          title="Default groups"
          customClass="utmDefaultGroupsModal"
          dialogStyle={{ maxWidth: 520 }}
          showFooter={false}
          onCloseClick={() => this.setState({ isDefaultGroupsOpen: false })}
        >
          <TournamentDefaultGroupsForm
            tournament={{ ...this.props.tournament, customGroups: groups }}
            onSubmit={this.saveDefaultGroups}
            onCancel={() => this.setState({ isDefaultGroupsOpen: false })}
          />
        </SimpleModal>
        <SimpleModal
          isOpen={this.state.isAddCustomGroupOpen}
          title="Add custom group"
          customClass="utmGroupFormModal"
          dialogStyle={{ maxWidth: 460 }}
          showFooter={false}
          isBodyBold={false}
          onCloseClick={() => this.setState({ isAddCustomGroupOpen: false, groupFormError: '' })}
        >
          <form
            className="utmGroupForm"
            onSubmit={event => {
              event.preventDefault();
              if (!this.state.isCreatingCustomGroup) this.createCustomGroup();
            }}
          >
            <label>
              <span>Group name</span>
              <input
                type="text"
                value={this.state.newGroupName}
                onChange={event => this.setState({ newGroupName: event.target.value, groupFormError: '' })}
                placeholder="Enter group name"
                autoFocus
              />
            </label>
            <label>
              <span>Level</span>
              <select
                value={this.state.newGroupStageId}
                onChange={event => this.setState({ newGroupStageId: event.target.value, groupFormError: '' })}
              >
                <option value="">Select level</option>
                {(this.props.tournament.tournamentLayout || []).map(level => (
                  <option key={level.id} value={level.id}>
                    {level.name}
                  </option>
                ))}
              </select>
            </label>
            {this.state.groupFormError ? <div className="utmGroupForm__error">{this.state.groupFormError}</div> : null}
            <footer>
              <button
                type="button"
                className="is-secondary"
                onClick={() => this.setState({ isAddCustomGroupOpen: false, groupFormError: '' })}
              >
                Cancel
              </button>
              <button type="submit" className="is-primary" disabled={this.state.isCreatingCustomGroup}>
                {this.state.isCreatingCustomGroup ? 'Adding…' : 'Add group'}
              </button>
            </footer>
          </form>
        </SimpleModal>
        {this.state.openedGroup && !this.state.isDiscardGroupChangesOpen ? (
          <div
            className="utmConfirmationDrawer utmGroupDrawer"
            onMouseDown={event => event.target === event.currentTarget && this.requestCloseGroupDrawer()}
          >
            <aside>
              <header>
                <div>
                  <h3>{this.state.openedGroup.name || 'Unnamed group'}</h3>
                  <p>Group details</p>
                </div>
                <button type="button" onClick={this.requestCloseGroupDrawer} aria-label="Close group details">
                  ×
                </button>
              </header>
              <div className="utmConfirmationDrawer__body utmGroupDrawer__body">
                <label>
                  <span>Group name</span>
                  <input
                    type="text"
                    value={this.state.editedGroupName}
                    onChange={event => this.setState({ editedGroupName: event.target.value, groupFormError: '' })}
                  />
                </label>
                <label>
                  <span>Level</span>
                  <select
                    value={this.state.editedGroupStageId}
                    onChange={event => this.setState({ editedGroupStageId: event.target.value, groupFormError: '' })}
                  >
                    <option value="">Select level</option>
                    {(this.props.tournament.tournamentLayout || []).map(level => (
                      <option key={level.id} value={level.id}>
                        {level.name}
                      </option>
                    ))}
                  </select>
                </label>
                {this.state.groupFormError ? (
                  <div className="utmGroupForm__error">{this.state.groupFormError}</div>
                ) : null}
              </div>
              <footer>
                <button type="button" className="is-secondary" onClick={this.requestCloseGroupDrawer}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="is-primary"
                  disabled={this.state.isSavingGroup || !this.hasUnsavedGroupChanges()}
                  onClick={this.saveGroup}
                >
                  {this.state.isSavingGroup ? 'Saving…' : 'Save changes'}
                </button>
              </footer>
            </aside>
          </div>
        ) : null}
        <UnsavedChangesModal
          isOpen={this.state.isDiscardGroupChangesOpen}
          message="Your changes to this group have not been saved. If you leave now, they will be lost."
          onKeepEditing={() => this.setState({ isDiscardGroupChangesOpen: false })}
          onDiscard={this.discardGroupChanges}
        />
        <SimpleModal
          isOpen={this.state.isDeleteGroupsOpen}
          title={this.state.deleteGroupsError ? 'Groups cannot be deleted' : 'Delete selected groups?'}
          customClass={`unionTournamentDeleteModal utmGroupDeleteModal${
            this.state.deleteGroupsError ? ' is-blocked' : ''
          }`}
          dialogStyle={{ maxWidth: 440 }}
          buttonText={this.state.deleteGroupsError ? 'Close' : this.state.isDeletingGroups ? 'Deleting…' : 'Delete'}
          hideCancelButton={Boolean(this.state.deleteGroupsError)}
          onButtonClick={
            this.state.deleteGroupsError
              ? () => this.setState({ isDeleteGroupsOpen: false, deleteGroupsError: '' })
              : this.deleteSelectedGroups
          }
          onCloseClick={() => this.setState({ isDeleteGroupsOpen: false, deleteGroupsError: '' })}
        >
          <p className="utmGroupDeleteModal__groups">{selectedGroups.map(group => group.name).join(', ')}</p>
          {this.state.deleteGroupsError ? (
            <div className="utmGroupDeleteError" role="alert">
              {this.state.deleteGroupsError}
            </div>
          ) : (
            <p>Groups can only be deleted when no teams or tournament events are linked to them.</p>
          )}
        </SimpleModal>
      </>
    );
  };

  render() {
    const isTeamCompetition = this.isTeamCompetition();
    const sections = isTeamCompetition ? TEAM_SECTIONS : INDIVIDUAL_SECTIONS;
    const activeSection = sections.some(section => section.key === this.props.activeSection)
      ? this.props.activeSection
      : sections[0].key;
    const activeLabel = sections.find(section => section.key === activeSection)?.label || 'Competition';
    return (
      <main className="utmCompetition">
        <nav className="utmCompetition__subtabs" aria-label="Competition sections">
          {sections.map(section => (
            <button
              key={section.key}
              type="button"
              className={section.key === activeSection ? 'is-active' : ''}
              aria-current={section.key === activeSection ? 'page' : undefined}
              onClick={() => this.props.onSectionChange(section.key)}
            >
              <Icon path={section.icon} />
              <span>{section.label}</span>
            </button>
          ))}
        </nav>
        <div className="utmCompetition__content">
          {isTeamCompetition && activeSection === 'teams' && this.state.unassignedTeams > 0 ? (
            <div className="utmCompetition__warning" role="status">
              <Icon path="M12 9v4M12 17h.01M10.3 3.9 2.4 17.6A2 2 0 0 0 4.1 20h15.8a2 2 0 0 0 1.7-2.4L13.7 3.9a2 2 0 0 0-3.4 0Z" />
              <div>
                <strong>
                  {this.state.unassignedTeams} {this.state.unassignedTeams === 1 ? 'team is' : 'teams are'} not assigned
                  to a group
                </strong>
                <p>Assign every team to a group before building the competition schedule.</p>
              </div>
            </div>
          ) : null}
          {!isTeamCompetition && activeSection === 'competitors' ? (
            <article className="utmCompetition__panel">
              <TournamentCompetitorsSummary user={this.props.user} tournamentId={this.props.tournament.id} />
            </article>
          ) : activeSection === 'teams' ? (
            this.renderTeams()
          ) : isTeamCompetition && activeSection === 'groups' ? (
            isTeamTournament(this.props.tournament)
              ? <TournamentCompetitionStructure user={this.props.user} tournament={this.props.tournament} standalone onUpdated={this.props.onUpdated} />
              : this.renderGroups()
          ) : isTeamCompetition && activeSection === 'pitches' ? (
            this.renderPitches()
          ) : (isTeamCompetition && activeSection === 'fixtures') ||
            (!isTeamCompetition && activeSection === 'events') ? (
            this.renderEvents()
          ) : (
            <article className="utmCompetition__panel">
              <header>
                <h2>{activeLabel}</h2>
              </header>
              <div className="utmCompetition__placeholder">
                The new {activeLabel.toLowerCase()} workspace will be added here.
              </div>
            </article>
          )}
        </div>
      </main>
    );
  }
}
